import type { AppData } from "../types/domain";

// Canonical fictional fixtures and route specifications for frontend-first Proctor development.

export const APP_DATA = {
  areas: [
    {
      id: "command",
      label: "Proctor Command",
      shortLabel: "Command",
      icon: "home",
      accent: "violet",
      routes: [
        {
          id: "live-control-room",
          title: "Live Control Room",
          description: "Supervise every assigned assessment from one human-led, privacy-aware control room.",
          group: "Proctor Command",
          icon: "monitor",
          badge: "8",
        },
        {
          id: "priority-signals",
          title: "Priority Signals",
          description:
            "Triage technical, identity, accessibility, environment and wellbeing signals without treating signals as findings.",
          group: "Proctor Command",
          icon: "alert",
          badge: "12",
        },
        {
          id: "todays-schedule",
          title: "Today’s Schedule",
          description:
            "See upcoming Academy, Chatoner Class and Chatoner AI Labs sessions with codes, roles and readiness.",
          group: "Proctor Command",
          icon: "calendar",
        },
        {
          id: "proctor-workload",
          title: "Proctor Workload",
          description: "Assigned rooms, relief coverage, handovers, breaks and response-time commitments.",
          group: "Proctor Command",
          icon: "users",
        },
        {
          id: "connection-health",
          title: "Connection Health",
          description:
            "Monitor Academy, Class and Labs handoff health, candidate connectivity and service status.",
          group: "Proctor Command",
          icon: "pulse",
        },
      ],
    },
    {
      id: "sessions",
      label: "Sessions & Assignments",
      shortLabel: "Sessions",
      icon: "calendar",
      accent: "blue",
      routes: [
        {
          id: "scheduled-sessions",
          title: "Scheduled Sessions",
          description: "All assigned and available assessment sessions across Academy, Class and Labs.",
          group: "Sessions",
          icon: "calendar",
        },
        {
          id: "my-assignments",
          title: "My Assignments",
          description: "Your confirmed lead, room, relief, accessibility and handover responsibilities.",
          group: "Sessions",
          icon: "clipboard",
          badge: "6",
        },
        {
          id: "session-details",
          title: "Session Details",
          description:
            "Assessment scope, candidates, approved materials, accommodations, timing and escalation contacts.",
          group: "Sessions",
          icon: "file",
        },
        {
          id: "proctor-calendar",
          title: "Proctor Calendar",
          description: "Assessment shifts, rehearsals, preflight windows, breaks, handovers and training.",
          group: "Scheduling",
          icon: "calendar",
        },
        {
          id: "proctor-teams",
          title: "Proctor Teams",
          description:
            "Lead proctors, room proctors, relief staff, technical support, accessibility and safeguarding contacts.",
          group: "People",
          icon: "users",
        },
        {
          id: "handover-notes",
          title: "Handover Notes",
          description:
            "Structured, factual room handovers that preserve context without speculating about outcomes.",
          group: "Operations",
          icon: "message",
          badge: "3",
        },
      ],
    },
    {
      id: "readiness",
      label: "Candidate Readiness",
      shortLabel: "Readiness",
      icon: "check",
      accent: "cyan",
      routes: [
        {
          id: "readiness-dashboard",
          title: "Readiness Dashboard",
          description:
            "Identity, device, network, room, materials, accommodations and consent readiness at a glance.",
          group: "Readiness",
          icon: "pulse",
          badge: "9",
        },
        {
          id: "candidate-roster",
          title: "Candidate Roster",
          description: "Search and filter large candidate groups with scoped, session-specific information.",
          group: "Candidates",
          icon: "users",
        },
        {
          id: "identity-verification",
          title: "Identity Verification",
          description: "Human verification workflow, approved alternatives and documented re-checks.",
          group: "Readiness Checks",
          icon: "user",
          badge: "4",
        },
        {
          id: "device-network-checks",
          title: "Device & Network Checks",
          description: "Camera, microphone, browser, bandwidth, power and fallback readiness.",
          group: "Readiness Checks",
          icon: "monitor",
          badge: "5",
        },
        {
          id: "room-environment-checks",
          title: "Room & Environment Checks",
          description: "Approved environment checks using proportionate, documented procedures.",
          group: "Readiness Checks",
          icon: "eye",
        },
        {
          id: "allowed-materials",
          title: "Allowed Materials",
          description: "Exam-specific reference sheets, calculators, tools, files and accessibility aids.",
          group: "Assessment Rules",
          icon: "book",
        },
        {
          id: "late-reentry-queue",
          title: "Late & Re-entry Queue",
          description: "Late arrivals, approved breaks, reconnects and re-entry decisions under policy.",
          group: "Exceptions",
          icon: "history",
          badge: "5",
        },
      ],
    },
    {
      id: "rooms",
      label: "Secure Exam Rooms",
      shortLabel: "Exam Rooms",
      icon: "shield",
      accent: "green",
      routes: [
        {
          id: "active-exam-room",
          title: "Active Exam Room",
          description:
            "Monitor the current room, candidate states, timing, signals, messages and human interventions.",
          group: "Secure Rooms",
          icon: "monitor",
          badge: "38",
        },
        {
          id: "room-overview",
          title: "Room Overview",
          description: "Session rules, platform, timing, proctor team, candidates and active controls.",
          group: "Secure Rooms",
          icon: "layout",
        },
        {
          id: "candidate-grid",
          title: "Candidate Grid",
          description:
            "Large, searchable and paginated candidate view with status, connectivity and accommodations.",
          group: "Secure Rooms",
          icon: "grid",
        },
        {
          id: "secure-messages",
          title: "Secure Messages",
          description:
            "Candidate, proctor-team, assessor, technical and safeguarding communications with audit context.",
          group: "Communication",
          icon: "message",
          badge: "7",
        },
        {
          id: "session-controls",
          title: "Session Controls",
          description:
            "Policy-authorized pause, resume, time extension, room broadcast, lock and emergency controls.",
          group: "Operations",
          icon: "sliders",
        },
        {
          id: "breaks-reentry",
          title: "Breaks & Re-entry",
          description: "Approved breaks, accessibility pauses, reconnects and identity re-checks.",
          group: "Operations",
          icon: "history",
        },
        {
          id: "emergency-procedures",
          title: "Emergency Procedures",
          description:
            "Technical outage, safety, wellbeing, evacuation, safeguarding and continuity playbooks.",
          group: "Safety",
          icon: "alert",
        },
      ],
    },
    {
      id: "class",
      label: "Chatoner Class",
      shortLabel: "Class",
      icon: "video",
      accent: "purple",
      routes: [
        {
          id: "class-connection",
          title: "Class Connection",
          description:
            "Independent class.chatoner.com connection for live, oral, panel and observed assessments.",
          group: "Chatoner Class",
          icon: "link",
        },
        {
          id: "oral-live-exams",
          title: "Oral & Live Exams",
          description: "Scheduled oral, presentation, defence, interview and live practical assessments.",
          group: "Chatoner Class",
          icon: "video",
          badge: "3",
        },
        {
          id: "class-exam-sessions",
          title: "Class Exam Sessions",
          description:
            "Session codes, invitation links, candidate scope, proctor assignment and return context.",
          group: "Chatoner Class",
          icon: "calendar",
        },
        {
          id: "panel-huddle-sessions",
          title: "Panel & Huddle Sessions",
          description: "Panel rooms, private deliberation huddles and authorized candidate waiting rooms.",
          group: "Chatoner Class",
          icon: "users",
        },
        {
          id: "attendance-presence",
          title: "Attendance & Presence",
          description: "Join, leave, late, reconnect and presence records for the assessment session.",
          group: "Chatoner Class",
          icon: "check",
        },
        {
          id: "recordings-captions",
          title: "Recordings & Captions",
          description: "Authorized recordings, captions, interpretation, transcripts, access and retention.",
          group: "Chatoner Class",
          icon: "play",
        },
        {
          id: "class-exam-policies",
          title: "Class Exam Policies",
          description:
            "Waiting-room, recording, identity, captions, language, safeguarding and evidence rules.",
          group: "Governance",
          icon: "shield",
        },
      ],
    },
    {
      id: "labs",
      label: "Chatoner AI Labs",
      shortLabel: "Labs",
      icon: "flask",
      accent: "aqua",
      routes: [
        {
          id: "labs-connection",
          title: "Labs Connection",
          description:
            "Independent labs.chatoner.com connection for observed practical and technical assessments.",
          group: "Chatoner AI Labs",
          icon: "link",
        },
        {
          id: "lab-assessment-sessions",
          title: "Lab Assessment Sessions",
          description:
            "Assigned practical exam sessions, Lab environments, candidates, entitlements and proctor scope.",
          group: "Chatoner AI Labs",
          icon: "calendar",
          badge: "4",
        },
        {
          id: "intended-lab-environments",
          title: "Intended Lab Environments",
          description:
            "Exact environments connected to each practical assessment without reproducing the Labs runtime.",
          group: "Chatoner AI Labs",
          icon: "flask",
        },
        {
          id: "active-practical-exams",
          title: "Active Practical Exams",
          description: "Aggregate session state, runtime safety signals and authorized live handoff to Labs.",
          group: "Live Operations",
          icon: "monitor",
          badge: "2",
        },
        {
          id: "returned-evidence",
          title: "Returned Evidence",
          description:
            "Consented and policy-approved evidence returned from Labs to the Academy assessment record.",
          group: "Evidence Sync",
          icon: "inbox",
        },
        {
          id: "runtime-safety-signals",
          title: "Runtime Safety Signals",
          description: "Compute, tool, network, environment and emergency-stop signals owned by Labs.",
          group: "Safety",
          icon: "shield",
        },
        {
          id: "labs-data-boundaries",
          title: "Labs Data Boundaries",
          description:
            "What Academy proctors can see, what remains private in Labs and how scope is independently verified.",
          group: "Governance",
          icon: "lock",
        },
      ],
    },
    {
      id: "incidents",
      label: "Incidents & Human Review",
      shortLabel: "Incidents",
      icon: "alert",
      accent: "orange",
      routes: [
        {
          id: "incident-queue",
          title: "Incident Queue",
          description:
            "Signals and factual events waiting for review, escalation or closure by authorized humans.",
          group: "Incident Management",
          icon: "inbox",
          badge: "9",
        },
        {
          id: "record-incident",
          title: "Record Incident",
          description:
            "Record observable facts, source, time, action, evidence and handover without declaring misconduct.",
          group: "Incident Management",
          icon: "plus",
        },
        {
          id: "evidence-review",
          title: "Evidence Review",
          description: "Review authorized event sequences and linked evidence before escalation or closure.",
          group: "Human Review",
          icon: "eye",
          badge: "5",
        },
        {
          id: "candidate-statements",
          title: "Candidate Statements",
          description: "Invite and retain the candidate’s explanation in their own words and language.",
          group: "Human Review",
          icon: "message",
        },
        {
          id: "escalations",
          title: "Escalations",
          description:
            "Route integrity, safeguarding, technical, accessibility and wellbeing matters to the correct authority.",
          group: "Human Review",
          icon: "arrow",
          badge: "3",
        },
        {
          id: "technical-incidents",
          title: "Technical Incidents",
          description:
            "Connectivity, power, browser, media, runtime and platform incidents separated from integrity findings.",
          group: "Incident Types",
          icon: "monitor",
        },
        {
          id: "closed-incidents",
          title: "Closed Incidents",
          description: "Resolved, dismissed, transferred and completed records with immutable history.",
          group: "Records",
          icon: "archive",
        },
      ],
    },
    {
      id: "evidence",
      label: "Evidence & Chain of Custody",
      shortLabel: "Evidence",
      icon: "database",
      accent: "blue",
      routes: [
        {
          id: "evidence-records",
          title: "Evidence Records",
          description:
            "Session-scoped evidence inventory with source, integrity hash, custodian, access and retention.",
          group: "Evidence",
          icon: "database",
        },
        {
          id: "session-timelines",
          title: "Session Timelines",
          description: "Chronological candidate, room, system, communication and human-action events.",
          group: "Evidence",
          icon: "history",
        },
        {
          id: "screenshots-clips",
          title: "Screenshots & Clips",
          description:
            "Authorized captures and clips with purpose, provenance, redaction and access control.",
          group: "Evidence",
          icon: "monitor",
        },
        {
          id: "device-network-logs",
          title: "Device & Network Logs",
          description: "Approved browser, connectivity and device telemetry retained under policy.",
          group: "Evidence",
          icon: "pulse",
        },
        {
          id: "chain-of-custody",
          title: "Chain of Custody",
          description: "Capture, hash, transfer, access, export and disposition history.",
          group: "Integrity",
          icon: "link",
        },
        {
          id: "retention-access",
          title: "Retention & Access",
          description: "Purpose limitation, legal hold, minimum retention, deletion and role-scoped access.",
          group: "Governance",
          icon: "lock",
        },
        {
          id: "export-packages",
          title: "Export Packages",
          description: "Manifested, permission-checked evidence packages for authorized reviewers.",
          group: "Evidence",
          icon: "download",
        },
      ],
    },
    {
      id: "accommodations",
      label: "Accommodations & Accessibility",
      shortLabel: "Access",
      icon: "accessibility",
      accent: "violet",
      routes: [
        {
          id: "approved-accommodations",
          title: "Approved Accommodations",
          description: "Session-scoped accommodations supplied by authorized accessibility staff.",
          group: "Accommodations",
          icon: "accessibility",
          badge: "12",
        },
        {
          id: "extra-time-breaks",
          title: "Extra Time & Breaks",
          description: "Pre-approved time, pause, break, rest and re-entry arrangements.",
          group: "Accommodations",
          icon: "clock",
        },
        {
          id: "assistive-technology",
          title: "Assistive Technology",
          description:
            "Screen readers, magnification, alternative input, approved aids and compatibility checks.",
          group: "Accommodations",
          icon: "monitor",
        },
        {
          id: "captions-interpretation",
          title: "Captions & Interpretation",
          description:
            "Captions, sign-language interpretation, language support and communication preferences.",
          group: "Accommodations",
          icon: "message",
        },
        {
          id: "alternative-verification",
          title: "Alternative Verification",
          description: "Approved non-standard identity and environment verification procedures.",
          group: "Accommodations",
          icon: "user",
        },
        {
          id: "accessibility-incidents",
          title: "Accessibility Incidents",
          description: "Failures or barriers requiring immediate support and independent follow-up.",
          group: "Accommodations",
          icon: "alert",
          badge: "2",
        },
      ],
    },
    {
      id: "communications",
      label: "Communications & Support",
      shortLabel: "Support",
      icon: "message",
      accent: "cyan",
      routes: [
        {
          id: "secure-communications",
          title: "Secure Communications",
          description: "Session-scoped messages with candidates, proctor teams and authorized staff.",
          group: "Communication",
          icon: "message",
          badge: "7",
        },
        {
          id: "candidate-help-queue",
          title: "Candidate Help Queue",
          description: "Technical, procedural, accessibility and wellbeing requests during assessments.",
          group: "Support",
          icon: "help",
          badge: "6",
        },
        {
          id: "instructor-assessor-liaison",
          title: "Instructor & Assessor Liaison",
          description:
            "Clarifications and escalations without exposing answers or inviting grading by the proctor.",
          group: "Support",
          icon: "teacher",
        },
        {
          id: "technical-support",
          title: "Technical Support",
          description: "Platform, device, network, audio, video, runtime and recovery assistance.",
          group: "Support",
          icon: "settings",
        },
        {
          id: "announcements",
          title: "Announcements",
          description: "Policy-approved room and session broadcasts with audience preview.",
          group: "Communication",
          icon: "bell",
        },
        {
          id: "emergency-contacts",
          title: "Emergency Contacts",
          description: "Named assessment, technical, accessibility, safeguarding and wellbeing contacts.",
          group: "Support",
          icon: "alert",
        },
      ],
    },
    {
      id: "reports",
      label: "Reports & Quality",
      shortLabel: "Reports",
      icon: "chart",
      accent: "green",
      routes: [
        {
          id: "session-reports",
          title: "Session Reports",
          description: "Complete proctor report, attendance, timing, interventions, incidents and handover.",
          group: "Reports",
          icon: "file",
        },
        {
          id: "incident-analytics",
          title: "Incident Analytics",
          description: "Privacy-aware patterns by event category, platform, resolution and response time.",
          group: "Analytics",
          icon: "chart",
        },
        {
          id: "technical-reliability",
          title: "Technical Reliability",
          description: "Connectivity, browser, media, Class, Labs and Academy reliability.",
          group: "Analytics",
          icon: "pulse",
        },
        {
          id: "accommodation-delivery",
          title: "Accommodation Delivery",
          description: "Whether approved accommodations were prepared and delivered correctly.",
          group: "Quality",
          icon: "accessibility",
        },
        {
          id: "proctor-performance",
          title: "Proctor Performance",
          description:
            "Coverage, response, documentation, handovers, training and quality review—not learner outcomes.",
          group: "Quality",
          icon: "target",
        },
        {
          id: "report-builder",
          title: "Report Builder",
          description: "Build governed reports using approved fields, thresholds and access scope.",
          group: "Reports",
          icon: "sliders",
        },
        {
          id: "scheduled-reports",
          title: "Scheduled Reports",
          description: "Recurring secure reports for assessment operations and quality teams.",
          group: "Reports",
          icon: "calendar",
        },
        {
          id: "quality-reviews",
          title: "Quality Reviews",
          description: "Human sampling, calibration, feedback, corrective action and continuous improvement.",
          group: "Quality",
          icon: "check",
        },
      ],
    },
    {
      id: "governance",
      label: "Policies, Training & Settings",
      shortLabel: "Governance",
      icon: "shield",
      accent: "orange",
      routes: [
        {
          id: "proctoring-policies",
          title: "Proctoring Policies",
          description: "Approved procedures by assessment type, platform, age, location and risk level.",
          group: "Policy",
          icon: "book",
        },
        {
          id: "authority-boundaries",
          title: "Integrity & Authority Boundaries",
          description:
            "What a proctor may do, what requires another role and what no automated signal may decide.",
          group: "Policy",
          icon: "shield",
        },
        {
          id: "privacy-data-minimization",
          title: "Privacy & Data Minimization",
          description: "Purpose, consent, least visibility, redaction, retention and candidate rights.",
          group: "Trust",
          icon: "lock",
        },
        {
          id: "safeguarding",
          title: "Safeguarding",
          description: "Age-aware procedures, protected escalation and restricted evidence access.",
          group: "Trust",
          icon: "heart",
        },
        {
          id: "proctor-certification",
          title: "Proctor Certification",
          description:
            "Required training, assessments, expiry, refreshers and platform-specific authorization.",
          group: "Training",
          icon: "certificate",
        },
        {
          id: "practice-simulation",
          title: "Practice Simulation",
          description:
            "Safe rehearsal of joins, signals, interventions, evidence and handover without real candidates.",
          group: "Training",
          icon: "play",
        },
        {
          id: "language-accessibility",
          title: "Language & Accessibility",
          description:
            "Primary language, translation, captions, keyboard use, contrast and motion preferences.",
          group: "Preferences",
          icon: "globe",
        },
        {
          id: "notifications",
          title: "Notifications",
          description: "Assignment, live-room, incident, handover, training and system-health alerts.",
          group: "Preferences",
          icon: "bell",
        },
        {
          id: "security-sessions",
          title: "Security & Sessions",
          description: "MFA, session timeout, trusted devices, re-authentication and emergency revocation.",
          group: "Security",
          icon: "key",
        },
        {
          id: "connected-platform-settings",
          title: "Connected Platform Settings",
          description: "Academy, Class and Labs trust, handoff, return, scope and health settings.",
          group: "Security",
          icon: "link",
        },
        {
          id: "proctor-help",
          title: "Proctor Help",
          description: "Procedures, role guidance, contact paths and support resources.",
          group: "Support",
          icon: "help",
        },
      ],
    },
  ],
  sessions: [
    {
      id: "sess-app-2026-10-14",
      title: "AI Product Builder — Secure Applied Examination",
      platform: "Academy Secure Room",
      date: "14 Oct 2026",
      time: "14:00–16:00 EAT",
      code: "APB-7Q4M",
      candidates: 38,
      ready: 33,
      status: "Live",
      lead: "Amina Mutesi",
      role: "Lead proctor",
      program: "AI Product Builder",
      location: "Remote · Uganda cohort",
      risk: "Standard",
      recording: "Event evidence only",
    },
    {
      id: "sess-oral-2026-10-15",
      title: "Responsible AI — Oral Defence Panel",
      platform: "Chatoner Class",
      date: "15 Oct 2026",
      time: "09:00–12:30 EAT",
      code: "CLS-RAI-2841",
      candidates: 12,
      ready: 12,
      status: "Ready",
      lead: "Daniel Okello",
      role: "Panel proctor",
      program: "Responsible AI Practice",
      location: "class.chatoner.com",
      risk: "Moderate",
      recording: "Authorized recording + captions",
    },
    {
      id: "sess-lab-2026-10-16",
      title: "Agentic Workflow Practical Examination",
      platform: "Chatoner AI Labs",
      date: "16 Oct 2026",
      time: "13:00–16:00 EAT",
      code: "LAB-AWF-8820",
      candidates: 24,
      ready: 21,
      status: "Preflight",
      lead: "Grace Namusoke",
      role: "Practical proctor",
      program: "AI Automation & Workflows",
      location: "labs.chatoner.com",
      risk: "Elevated compute",
      recording: "Labs evidence policy",
    },
    {
      id: "sess-data-2026-10-17",
      title: "Data & Predictive AI Practical",
      platform: "Chatoner AI Labs",
      date: "17 Oct 2026",
      time: "10:00–13:00 EAT",
      code: "LAB-DPA-5913",
      candidates: 31,
      ready: 27,
      status: "Preflight",
      lead: "Robert Ssemanda",
      role: "Room proctor",
      program: "Data & Predictive AI",
      location: "labs.chatoner.com",
      risk: "Standard",
      recording: "Labs evidence policy",
    },
    {
      id: "sess-prompt-2026-10-18",
      title: "Prompt & Context Engineering Assessment",
      platform: "Academy Secure Room",
      date: "18 Oct 2026",
      time: "08:30–10:30 EAT",
      code: "PCE-4L9N",
      candidates: 42,
      ready: 39,
      status: "Scheduled",
      lead: "Amina Mutesi",
      role: "Lead proctor",
      program: "Prompt & Context Engineering",
      location: "Remote · multi-country",
      risk: "Standard",
      recording: "Event evidence only",
    },
    {
      id: "sess-interview-2026-10-19",
      title: "Applied AI Portfolio Interview",
      platform: "Chatoner Class",
      date: "19 Oct 2026",
      time: "11:00–15:00 EAT",
      code: "CLS-API-7190",
      candidates: 8,
      ready: 8,
      status: "Scheduled",
      lead: "Esther Nakiwala",
      role: "Interview proctor",
      program: "Applied AI Portfolio",
      location: "class.chatoner.com",
      risk: "Standard",
      recording: "Authorized recording",
    },
    {
      id: "sess-foundation-2026-10-20",
      title: "AI Foundations Final Examination",
      platform: "Academy Secure Room",
      date: "20 Oct 2026",
      time: "14:00–16:00 EAT",
      code: "AIF-8P2X",
      candidates: 64,
      ready: 54,
      status: "Scheduled",
      lead: "Samuel Kato",
      role: "Lead proctor",
      program: "AI Foundations",
      location: "Remote · East Africa",
      risk: "Large cohort",
      recording: "Event evidence only",
    },
    {
      id: "sess-access-2026-10-21",
      title: "Accessible AI Product Review",
      platform: "Chatoner Class",
      date: "21 Oct 2026",
      time: "10:00–12:00 EAT",
      code: "CLS-A11Y-3026",
      candidates: 10,
      ready: 9,
      status: "Scheduled",
      lead: "Lydia Atim",
      role: "Accessibility proctor",
      program: "AI Product Design",
      location: "class.chatoner.com",
      risk: "Accommodation-sensitive",
      recording: "Captions + authorized recording",
    },
  ],
  candidates: [
    {
      id: "cand-001",
      name: "Linda Nsubuga",
      candidateNo: "CND-2026001",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "87 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-002",
      name: "Sarah Achieng",
      candidateNo: "CND-2026002",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "86 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-003",
      name: "Joel Kato",
      candidateNo: "CND-2026003",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "85 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-004",
      name: "Mariam Nabwire",
      candidateNo: "CND-2026004",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "84 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-005",
      name: "David Ochieng",
      candidateNo: "CND-2026005",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "83 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-006",
      name: "Gloria Namuli",
      candidateNo: "CND-2026006",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Good",
      camera: false,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "82 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-007",
      name: "Brian Okello",
      candidateNo: "CND-2026007",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "81 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-008",
      name: "Ruth Nakato",
      candidateNo: "CND-2026008",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "80 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-009",
      name: "Isaac Ssebuuma",
      candidateNo: "CND-2026009",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Excellent",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "79 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-010",
      name: "Joan Akello",
      candidateNo: "CND-2026010",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "78 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-011",
      name: "Peter Mugisha",
      candidateNo: "CND-2026011",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "77 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-012",
      name: "Aisha Nanyonjo",
      candidateNo: "CND-2026012",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "76 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-013",
      name: "Samuel Odongo",
      candidateNo: "CND-2026013",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "75 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-014",
      name: "Faith Namusoke",
      candidateNo: "CND-2026014",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "74 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-015",
      name: "Kevin Ssempijja",
      candidateNo: "CND-2026015",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "73 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-016",
      name: "Mercy Atukunda",
      candidateNo: "CND-2026016",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Fair",
      camera: false,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "72 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-017",
      name: "Andrew Kizza",
      candidateNo: "CND-2026017",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "71 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-018",
      name: "Patience Nambasa",
      candidateNo: "CND-2026018",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "70 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-019",
      name: "John Bosco Lule",
      candidateNo: "CND-2026019",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "87 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-020",
      name: "Stella Nankya",
      candidateNo: "CND-2026020",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Fair",
      camera: false,
      microphone: false,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "86 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-021",
      name: "Hassan Kiyingi",
      candidateNo: "CND-2026021",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "85 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-022",
      name: "Diana Auma",
      candidateNo: "CND-2026022",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "84 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-023",
      name: "Mark Tumusiime",
      candidateNo: "CND-2026023",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "83 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-024",
      name: "Christine Ajok",
      candidateNo: "CND-2026024",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "82 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-025",
      name: "Henry Walusimbi",
      candidateNo: "CND-2026025",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "81 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-026",
      name: "Rebecca Nansubuga",
      candidateNo: "CND-2026026",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Good",
      camera: false,
      microphone: true,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "80 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-027",
      name: "Joseph Ekwaro",
      candidateNo: "CND-2026027",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "79 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-028",
      name: "Agnes Nambooze",
      candidateNo: "CND-2026028",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "78 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-029",
      name: "Moses Lubega",
      candidateNo: "CND-2026029",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Excellent",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "77 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-030",
      name: "Juliet Aber",
      candidateNo: "CND-2026030",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "76 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-031",
      name: "Ronald Ssenyonga",
      candidateNo: "CND-2026031",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "75 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-032",
      name: "Brenda Adoch",
      candidateNo: "CND-2026032",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "74 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-033",
      name: "Emmanuel Kaggwa",
      candidateNo: "CND-2026033",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "73 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-034",
      name: "Sharon Nakaweesi",
      candidateNo: "CND-2026034",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "72 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-035",
      name: "Nicholas Otema",
      candidateNo: "CND-2026035",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "71 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-036",
      name: "Catherine Birungi",
      candidateNo: "CND-2026036",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Fair",
      camera: false,
      microphone: true,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "70 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-037",
      name: "Michael Wekesa",
      candidateNo: "CND-2026037",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "87 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-038",
      name: "Irene Nalubega",
      candidateNo: "CND-2026038",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "86 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-039",
      name: "Paul Komakech",
      candidateNo: "CND-2026039",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "85 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-040",
      name: "Sylvia Nantongo",
      candidateNo: "CND-2026040",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Fair",
      camera: false,
      microphone: false,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "84 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-041",
      name: "Allan Kintu",
      candidateNo: "CND-2026041",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "83 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-042",
      name: "Rose Atim",
      candidateNo: "CND-2026042",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "82 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-043",
      name: "James Ssekandi",
      candidateNo: "CND-2026043",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "81 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-044",
      name: "Hadijah Namata",
      candidateNo: "CND-2026044",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "80 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-045",
      name: "George Ouma",
      candidateNo: "CND-2026045",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "79 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-046",
      name: "Lilian Apio",
      candidateNo: "CND-2026046",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Good",
      camera: false,
      microphone: true,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "78 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-047",
      name: "Martin Byaruhanga",
      candidateNo: "CND-2026047",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "77 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-048",
      name: "Evelyn Nabirye",
      candidateNo: "CND-2026048",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "76 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-049",
      name: "Fred Kabuye",
      candidateNo: "CND-2026049",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Excellent",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "75 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-050",
      name: "Beatrice Awor",
      candidateNo: "CND-2026050",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "74 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-051",
      name: "Arthur Kakooza",
      candidateNo: "CND-2026051",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "73 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-052",
      name: "Nancy Akumu",
      candidateNo: "CND-2026052",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "72 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-053",
      name: "Stephen Nsubuga",
      candidateNo: "CND-2026053",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "71 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-054",
      name: "Harriet Nankunda",
      candidateNo: "CND-2026054",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "70 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-055",
      name: "Denis Opio",
      candidateNo: "CND-2026055",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "87 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-056",
      name: "Sophia Kiconco",
      candidateNo: "CND-2026056",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Fair",
      camera: false,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "86 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-057",
      name: "Francis Muwanga",
      candidateNo: "CND-2026057",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "85 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-058",
      name: "Doreen Alupo",
      candidateNo: "CND-2026058",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "84 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-059",
      name: "Patrick Sserunjogi",
      candidateNo: "CND-2026059",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "83 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-060",
      name: "Monica Namutebi",
      candidateNo: "CND-2026060",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Fair",
      camera: false,
      microphone: false,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "82 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-061",
      name: "Charles Odongo",
      candidateNo: "CND-2026061",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "81 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-062",
      name: "Priscilla Nakiwala",
      candidateNo: "CND-2026062",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "80 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-063",
      name: "Linda Nsubuga 2",
      candidateNo: "CND-2026063",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "79 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-064",
      name: "Sarah Achieng 2",
      candidateNo: "CND-2026064",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "78 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-065",
      name: "Joel Kato 2",
      candidateNo: "CND-2026065",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "77 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-066",
      name: "Mariam Nabwire 2",
      candidateNo: "CND-2026066",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Good",
      camera: false,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "76 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-067",
      name: "David Ochieng 2",
      candidateNo: "CND-2026067",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "75 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-068",
      name: "Gloria Namuli 2",
      candidateNo: "CND-2026068",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "74 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-069",
      name: "Brian Okello 2",
      candidateNo: "CND-2026069",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Excellent",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "73 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-070",
      name: "Ruth Nakato 2",
      candidateNo: "CND-2026070",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "72 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-071",
      name: "Isaac Ssebuuma 2",
      candidateNo: "CND-2026071",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "71 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-072",
      name: "Joan Akello 2",
      candidateNo: "CND-2026072",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "70 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-073",
      name: "Peter Mugisha 2",
      candidateNo: "CND-2026073",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "87 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-074",
      name: "Aisha Nanyonjo 2",
      candidateNo: "CND-2026074",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "86 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-075",
      name: "Samuel Odongo 2",
      candidateNo: "CND-2026075",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "85 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-076",
      name: "Faith Namusoke 2",
      candidateNo: "CND-2026076",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Fair",
      camera: false,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "84 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-077",
      name: "Kevin Ssempijja 2",
      candidateNo: "CND-2026077",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "83 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-078",
      name: "Mercy Atukunda 2",
      candidateNo: "CND-2026078",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "82 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-079",
      name: "Andrew Kizza 2",
      candidateNo: "CND-2026079",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "81 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-080",
      name: "Patience Nambasa 2",
      candidateNo: "CND-2026080",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Fair",
      camera: false,
      microphone: false,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "80 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-081",
      name: "John Bosco Lule 2",
      candidateNo: "CND-2026081",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "79 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-082",
      name: "Stella Nankya 2",
      candidateNo: "CND-2026082",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "78 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-083",
      name: "Hassan Kiyingi 2",
      candidateNo: "CND-2026083",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "77 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-084",
      name: "Diana Auma 2",
      candidateNo: "CND-2026084",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "76 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-085",
      name: "Mark Tumusiime 2",
      candidateNo: "CND-2026085",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "75 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-086",
      name: "Christine Ajok 2",
      candidateNo: "CND-2026086",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Good",
      camera: false,
      microphone: true,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "74 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-087",
      name: "Henry Walusimbi 2",
      candidateNo: "CND-2026087",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "73 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-088",
      name: "Rebecca Nansubuga 2",
      candidateNo: "CND-2026088",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "72 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-089",
      name: "Joseph Ekwaro 2",
      candidateNo: "CND-2026089",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Excellent",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "71 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-090",
      name: "Agnes Nambooze 2",
      candidateNo: "CND-2026090",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "70 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-091",
      name: "Moses Lubega 2",
      candidateNo: "CND-2026091",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "87 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-092",
      name: "Juliet Aber 2",
      candidateNo: "CND-2026092",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "86 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-093",
      name: "Ronald Ssenyonga 2",
      candidateNo: "CND-2026093",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "85 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-094",
      name: "Brenda Adoch 2",
      candidateNo: "CND-2026094",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "84 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-095",
      name: "Emmanuel Kaggwa 2",
      candidateNo: "CND-2026095",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "83 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-096",
      name: "Sharon Nakaweesi 2",
      candidateNo: "CND-2026096",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Fair",
      camera: false,
      microphone: true,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "82 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-097",
      name: "Nicholas Otema 2",
      candidateNo: "CND-2026097",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "81 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-098",
      name: "Catherine Birungi 2",
      candidateNo: "CND-2026098",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "80 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-099",
      name: "Michael Wekesa 2",
      candidateNo: "CND-2026099",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "79 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-100",
      name: "Irene Nalubega 2",
      candidateNo: "CND-2026100",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Fair",
      camera: false,
      microphone: false,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "78 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-101",
      name: "Paul Komakech 2",
      candidateNo: "CND-2026101",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "77 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-102",
      name: "Sylvia Nantongo 2",
      candidateNo: "CND-2026102",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "76 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-103",
      name: "Allan Kintu 2",
      candidateNo: "CND-2026103",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "75 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-104",
      name: "Rose Atim 2",
      candidateNo: "CND-2026104",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "74 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-105",
      name: "James Ssekandi 2",
      candidateNo: "CND-2026105",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "73 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-106",
      name: "Hadijah Namata 2",
      candidateNo: "CND-2026106",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Good",
      camera: false,
      microphone: true,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "72 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-107",
      name: "George Ouma 2",
      candidateNo: "CND-2026107",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "71 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-108",
      name: "Lilian Apio 2",
      candidateNo: "CND-2026108",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "70 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-109",
      name: "Martin Byaruhanga 2",
      candidateNo: "CND-2026109",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Excellent",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "87 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-110",
      name: "Evelyn Nabirye 2",
      candidateNo: "CND-2026110",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "86 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-111",
      name: "Fred Kabuye 2",
      candidateNo: "CND-2026111",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "85 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-112",
      name: "Beatrice Awor 2",
      candidateNo: "CND-2026112",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "84 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-113",
      name: "Arthur Kakooza 2",
      candidateNo: "CND-2026113",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "English",
      timeRemaining: "83 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-114",
      name: "Nancy Akumu 2",
      candidateNo: "CND-2026114",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Luganda",
      timeRemaining: "82 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-115",
      name: "Stephen Nsubuga 2",
      candidateNo: "CND-2026115",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Focus change",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "Kiswahili",
      timeRemaining: "81 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-116",
      name: "Harriet Nankunda 2",
      candidateNo: "CND-2026116",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Reconnect",
      online: true,
      network: "Fair",
      camera: false,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Extra time 25%",
      language: "French",
      timeRemaining: "80 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-117",
      name: "Denis Opio 2",
      candidateNo: "CND-2026117",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Identity re-check",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "English",
      timeRemaining: "79 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-118",
      name: "Sophia Kiconco 2",
      candidateNo: "CND-2026118",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Approved break",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Caption support",
      language: "Luganda",
      timeRemaining: "78 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-119",
      name: "Francis Muwanga 2",
      candidateNo: "CND-2026119",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Technical pause",
      online: true,
      network: "Good",
      camera: false,
      microphone: false,
      identity: "Verified",
      accommodation: "Approved break plan",
      language: "Kiswahili",
      timeRemaining: "77 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-120",
      name: "Doreen Alupo 2",
      candidateNo: "CND-2026120",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Waiting",
      online: false,
      network: "Fair",
      camera: false,
      microphone: false,
      identity: "Re-check required",
      accommodation: "None",
      language: "French",
      timeRemaining: "76 min",
      lastEvent: "Human check completed",
    },
    {
      id: "cand-121",
      name: "Patrick Sserunjogi 2",
      candidateNo: "CND-2026121",
      program: "AI Product Builder",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Excellent",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "None",
      language: "English",
      timeRemaining: "75 min",
      lastEvent: "Answer saved",
    },
    {
      id: "cand-122",
      name: "Monica Namutebi 2",
      candidateNo: "CND-2026122",
      program: "Prompt & Context Engineering",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Extra time 25%",
      language: "Luganda",
      timeRemaining: "74 min",
      lastEvent: "Presence confirmed",
    },
    {
      id: "cand-123",
      name: "Charles Odongo 2",
      candidateNo: "CND-2026123",
      program: "AI Automation & Workflows",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Good",
      camera: true,
      microphone: true,
      identity: "Verified",
      accommodation: "Screen reader",
      language: "Kiswahili",
      timeRemaining: "73 min",
      lastEvent: "Network stable",
    },
    {
      id: "cand-124",
      name: "Priscilla Nakiwala 2",
      candidateNo: "CND-2026124",
      program: "Data & Predictive AI",
      session: "sess-app-2026-10-14",
      status: "Normal",
      online: true,
      network: "Fair",
      camera: true,
      microphone: true,
      identity: "Re-check required",
      accommodation: "Caption support",
      language: "French",
      timeRemaining: "72 min",
      lastEvent: "Human check completed",
    },
  ],
  incidents: [
    {
      id: "INC-2026-0041",
      candidate: "Linda Nsubuga",
      category: "Window focus",
      severity: "Review",
      status: "Needs review",
      time: "14:44:18",
      source: "Academy Secure Room",
      summary: "Window focus changed four times within seven minutes.",
      facts: "Four focus-change events were logged. No conclusion has been recorded.",
      action: "Candidate was reminded of the approved examination window procedure.",
      evidence: ["EV-2026-101", "EV-2026-102"],
      owner: "Amina Mutesi",
    },
    {
      id: "INC-2026-0042",
      candidate: "Sarah Achieng",
      category: "Connection",
      severity: "Technical",
      status: "Monitoring",
      time: "14:41:06",
      source: "Academy Secure Room",
      summary: "Candidate disconnected and began reconnecting.",
      facts: "Connection dropped for 46 seconds. Session and timer were preserved.",
      action: "Technical support opened and candidate re-entered through the approved path.",
      evidence: ["EV-2026-103"],
      owner: "Peter Ouma",
    },
    {
      id: "INC-2026-0043",
      candidate: "Joel Kato",
      category: "Identity",
      severity: "Review",
      status: "Resolved",
      time: "14:33:20",
      source: "Human verification",
      summary: "Identity re-check completed after image quality degraded.",
      facts: "Initial image quality was insufficient. Approved alternative verification was completed.",
      action: "Candidate resumed after human confirmation.",
      evidence: ["EV-2026-104"],
      owner: "Amina Mutesi",
    },
    {
      id: "INC-2026-0044",
      candidate: "Mariam Nabwire",
      category: "Room audio",
      severity: "Review",
      status: "Needs review",
      time: "14:29:02",
      source: "Human observation",
      summary: "A second voice was audible briefly.",
      facts: "A voice was audible for approximately three seconds. Content was not intelligible.",
      action: "Candidate was asked to confirm the room was clear; no outcome finding was made.",
      evidence: ["EV-2026-105"],
      owner: "Grace Namusoke",
    },
    {
      id: "INC-2026-0045",
      candidate: "David Ochieng",
      category: "Accessibility",
      severity: "Urgent",
      status: "Escalated",
      time: "14:22:11",
      source: "Candidate help",
      summary: "Screen-reader focus stopped moving within the secure question panel.",
      facts:
        "Candidate reported a keyboard-focus barrier. The timer was paused under approved accommodation policy.",
      action: "Accessibility support and assessor were notified; alternative delivery was opened.",
      evidence: ["EV-2026-106"],
      owner: "Lydia Atim",
    },
    {
      id: "INC-2026-0046",
      candidate: "Gloria Namuli",
      category: "Upload",
      severity: "Technical",
      status: "Resolved",
      time: "14:16:45",
      source: "Submission service",
      summary: "Practical evidence upload was delayed.",
      facts: "Upload retried twice and completed with the original timestamp preserved.",
      action: "No learner penalty; technical event retained separately.",
      evidence: ["EV-2026-107"],
      owner: "Peter Ouma",
    },
    {
      id: "INC-2026-0047",
      candidate: "Brian Okello",
      category: "Approved break",
      severity: "Information",
      status: "Resolved",
      time: "14:11:03",
      source: "Accommodation plan",
      summary: "Approved five-minute medical break started.",
      facts: "Break followed the pre-approved plan and timer adjustment.",
      action: "Candidate returned through the re-entry check.",
      evidence: ["EV-2026-108"],
      owner: "Amina Mutesi",
    },
    {
      id: "INC-2026-0048",
      candidate: "Ruth Nakato",
      category: "Materials",
      severity: "Review",
      status: "Closed",
      time: "14:04:37",
      source: "Human observation",
      summary: "Printed reference sheet was presented for confirmation.",
      facts: "Reference sheet matched the approved one-page formula sheet.",
      action: "Use was allowed and the check was recorded.",
      evidence: ["EV-2026-109"],
      owner: "Grace Namusoke",
    },
    {
      id: "INC-2026-0049",
      candidate: "Isaac Ssebuuma",
      category: "Wellbeing",
      severity: "Protected",
      status: "Escalated",
      time: "13:58:10",
      source: "Candidate help",
      summary: "Candidate requested urgent wellbeing support.",
      facts: "Candidate reported distress and asked to stop. Details are restricted.",
      action: "Session paused; safeguarding and wellbeing contact took over.",
      evidence: ["EV-2026-110"],
      owner: "Safeguarding team",
    },
  ],
  evidence: [
    {
      id: "EV-2026-101",
      type: "Event sequence",
      candidate: "Linda Nsubuga",
      captured: "14 Oct 2026 · 14:37–14:44",
      source: "Academy Secure Room",
      hash: "sha256:3ea4…9c21",
      custodian: "Academy Evidence Service",
      access: "Proctor + Integrity Reviewer",
      retention: "180 days",
      status: "Sealed",
    },
    {
      id: "EV-2026-102",
      type: "Proctor note",
      candidate: "Linda Nsubuga",
      captured: "14 Oct 2026 · 14:45",
      source: "Amina Mutesi",
      hash: "sha256:81ad…024f",
      custodian: "Assessment Operations",
      access: "Integrity Reviewer",
      retention: "180 days",
      status: "Sealed",
    },
    {
      id: "EV-2026-103",
      type: "Network log",
      candidate: "Sarah Achieng",
      captured: "14 Oct 2026 · 14:40–14:42",
      source: "Connection service",
      hash: "sha256:207e…11ac",
      custodian: "Academy Evidence Service",
      access: "Technical + Proctor",
      retention: "90 days",
      status: "Verified",
    },
    {
      id: "EV-2026-104",
      type: "Verification record",
      candidate: "Joel Kato",
      captured: "14 Oct 2026 · 14:32",
      source: "Human identity re-check",
      hash: "sha256:ff90…dd31",
      custodian: "Assessment Operations",
      access: "Restricted",
      retention: "180 days",
      status: "Sealed",
    },
    {
      id: "EV-2026-105",
      type: "Authorized audio clip",
      candidate: "Mariam Nabwire",
      captured: "14 Oct 2026 · 14:29",
      source: "Secure Room",
      hash: "sha256:b3a1…a511",
      custodian: "Academy Evidence Service",
      access: "Integrity Reviewer",
      retention: "180 days",
      status: "Sealed",
    },
    {
      id: "EV-2026-106",
      type: "Accessibility event",
      candidate: "David Ochieng",
      captured: "14 Oct 2026 · 14:22",
      source: "Candidate help + UI telemetry",
      hash: "sha256:64cc…f0a3",
      custodian: "Accessibility Office",
      access: "Accessibility + Assessor",
      retention: "Academic record policy",
      status: "Restricted",
    },
    {
      id: "EV-2026-107",
      type: "Upload retry log",
      candidate: "Gloria Namuli",
      captured: "14 Oct 2026 · 14:15–14:17",
      source: "Submission service",
      hash: "sha256:11ca…2fed",
      custodian: "Academy Evidence Service",
      access: "Technical + Assessor",
      retention: "90 days",
      status: "Verified",
    },
    {
      id: "EV-2026-108",
      type: "Approved break event",
      candidate: "Brian Okello",
      captured: "14 Oct 2026 · 14:11–14:16",
      source: "Accommodation workflow",
      hash: "sha256:a01e…3bb2",
      custodian: "Accessibility Office",
      access: "Proctor + Accessibility",
      retention: "Academic record policy",
      status: "Verified",
    },
  ],
  accommodations: [
    {
      candidate: "David Ochieng",
      type: "Screen reader + keyboard-only",
      session: "AI Product Builder Examination",
      owner: "Accessibility Office",
      status: "Ready",
      instructions: "Use alternative secure question panel; pause timer during accessibility recovery.",
    },
    {
      candidate: "Sarah Achieng",
      type: "Extra time 25%",
      session: "AI Product Builder Examination",
      owner: "Registry",
      status: "Applied",
      instructions: "Automatic additional 30 minutes; proctor may not alter without authorization.",
    },
    {
      candidate: "Mariam Nabwire",
      type: "Captions",
      session: "Responsible AI Oral Defence",
      owner: "Accessibility Office",
      status: "Ready",
      instructions: "Live captions enabled in Chatoner Class; transcript access restricted.",
    },
    {
      candidate: "Brian Okello",
      type: "Medical break plan",
      session: "AI Product Builder Examination",
      owner: "Student Support",
      status: "In use",
      instructions: "One five-minute break; approved re-entry check; timer adjustment automated.",
    },
    {
      candidate: "Ruth Nakato",
      type: "Approved formula sheet",
      session: "Data & Predictive AI Practical",
      owner: "Assessment Board",
      status: "Ready",
      instructions: "One-page formula sheet ID FS-DPA-2026-04.",
    },
    {
      candidate: "Aisha Nanyonjo",
      type: "Sign-language interpretation",
      session: "Applied AI Portfolio Interview",
      owner: "Accessibility Office",
      status: "Ready",
      instructions: "Interpreter assigned in Class; no access to private assessor notes.",
    },
    {
      candidate: "Samuel Odongo",
      type: "Low-motion interface",
      session: "Prompt Engineering Assessment",
      owner: "Accessibility Office",
      status: "Ready",
      instructions: "Reduced animation and non-flashing status signals.",
    },
    {
      candidate: "Faith Namusoke",
      type: "Alternative identity verification",
      session: "AI Foundations Final",
      owner: "Registry",
      status: "Ready",
      instructions: "Human document and spoken code verification; no automated biometric decision.",
    },
  ],
} as AppData;
export const PROCTOR_ROUTE_SPECS = {
  "live-control-room": {
    id: "live-control-room",
    area: "command",
    areaLabel: "Proctor Command",
    title: "Live Control Room",
    description: "Supervise every assigned assessment from one human-led, privacy-aware control room.",
    group: "Proctor Command",
    icon: "monitor",
    family: "live-command",
    source: "static",
    primary: { label: "Open active exam room", intent: "navigate:rooms:active-exam-room", icon: "monitor" },
    secondary: [
      { label: "Record incident", intent: "modal:record-incident", icon: "plus" },
      { label: "View today’s schedule", intent: "navigate:command:todays-schedule", icon: "calendar" },
    ],
    metrics: [
      { label: "Live rooms", value: "3", trend: "2 Class · 1 Academy", icon: "video", tone: "good" },
      { label: "Candidates active", value: "82", trend: "77 normal or ready", icon: "users", tone: "info" },
      { label: "Signals", value: "12", trend: "5 need human review", icon: "alert", tone: "warn" },
      { label: "Readiness", value: "94", trend: "9 checks outstanding", icon: "check", tone: "violet" },
    ],
    tabs: ["Live operations", "Priority timeline", "Next sessions"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-command-live-control-room-live-command",
    model: "special",
    layoutGroup: "dashboard",
  },
  "priority-signals": {
    id: "priority-signals",
    area: "command",
    areaLabel: "Proctor Command",
    title: "Priority Signals",
    description:
      "Triage technical, identity, accessibility, environment and wellbeing signals without treating signals as findings.",
    group: "Proctor Command",
    icon: "alert",
    family: "signal-triage",
    source: "incidents",
    primary: { label: "Record factual incident", intent: "modal:record-incident", icon: "plus" },
    secondary: [
      { label: "Open incident queue", intent: "navigate:incidents:incident-queue", icon: "alert" },
      { label: "View candidate context", intent: "navigate:readiness:candidate-roster", icon: "users" },
    ],
    metrics: [
      { label: "Urgent", value: "3", trend: "Immediate human attention", icon: "alert", tone: "danger" },
      { label: "Needs review", value: "9", trend: "No outcome assumed", icon: "eye", tone: "warn" },
      { label: "Technical", value: "4", trend: "Kept separate", icon: "pulse", tone: "info" },
      { label: "Resolved", value: "27", trend: "With named action", icon: "check", tone: "good" },
    ],
    tabs: ["Untriaged", "Monitoring", "Escalated", "Resolved"],
    columns: [
      { key: "id", label: "Signal / incident" },
      { key: "summary", label: "Observable event" },
      { key: "candidate", label: "Candidate" },
      { key: "severity", label: "Priority" },
      { key: "owner", label: "Human owner" },
    ],
    rows: [],
    notes: [
      {
        title: "A signal is not a finding",
        body: "Review event sequence, accessibility, approved accommodations and technical conditions before recording an incident.",
        icon: "shield",
      },
      {
        title: "Separate technical from integrity review",
        body: "A connection interruption must not silently become an academic-integrity allegation.",
        icon: "pulse",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-command-priority-signals-signal-triage",
    model: "queue",
    layoutGroup: "queue",
  },
  "todays-schedule": {
    id: "todays-schedule",
    area: "command",
    areaLabel: "Proctor Command",
    title: "Today’s Schedule",
    description:
      "See upcoming Academy, Chatoner Class and Chatoner AI Labs sessions with codes, roles and readiness.",
    group: "Proctor Command",
    icon: "calendar",
    family: "schedule-timeline",
    source: "sessions",
    primary: {
      label: "Open proctor calendar",
      intent: "navigate:sessions:proctor-calendar",
      icon: "calendar",
    },
    secondary: [
      { label: "View assignments", intent: "navigate:sessions:my-assignments", icon: "clipboard" },
      { label: "Start preflight", intent: "navigate:readiness:readiness-dashboard", icon: "check" },
    ],
    metrics: [
      { label: "Today", value: "4", trend: "Assigned sessions", icon: "calendar", tone: "info" },
      {
        label: "Preflight windows",
        value: "3",
        trend: "Before candidate entry",
        icon: "clock",
        tone: "warn",
      },
      { label: "Live now", value: "1", trend: "38 candidates", icon: "video", tone: "good" },
      { label: "Handover", value: "1", trend: "15:00 EAT", icon: "history", tone: "violet" },
    ],
    tabs: ["Timeline", "Platform", "Role & coverage"],
    columns: [
      { key: "time", label: "Time" },
      { key: "title", label: "Assessment" },
      { key: "platform", label: "Platform" },
      { key: "role", label: "Proctor role" },
      { key: "status", label: "Status" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-command-todays-schedule-schedule-timeline",
    model: "timeline",
    layoutGroup: "timeline",
  },
  "proctor-workload": {
    id: "proctor-workload",
    area: "command",
    areaLabel: "Proctor Command",
    title: "Proctor Workload",
    description: "Assigned rooms, relief coverage, handovers, breaks and response-time commitments.",
    group: "Proctor Command",
    icon: "users",
    family: "workload-board",
    source: "static",
    primary: { label: "Request relief coverage", intent: "modal:proctor-team", icon: "users" },
    secondary: [
      { label: "Open assignments", intent: "navigate:sessions:my-assignments", icon: "clipboard" },
      { label: "Create handover", intent: "modal:handover", icon: "history" },
    ],
    metrics: [
      { label: "Assigned rooms", value: "6", trend: "Across 4 days", icon: "monitor", tone: "info" },
      { label: "Lead duties", value: "2", trend: "Named authority", icon: "crown", tone: "violet" },
      { label: "Relief windows", value: "3", trend: "Confirmed", icon: "users", tone: "good" },
      { label: "Workload alerts", value: "1", trend: "Overlapping preflight", icon: "alert", tone: "warn" },
    ],
    tabs: ["My workload", "Team coverage", "Breaks & relief"],
    columns: [
      { key: "name", label: "Assignment" },
      { key: "detail", label: "Scope" },
      { key: "lane", label: "Workload lane" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Lead" },
    ],
    rows: [
      {
        name: "AI Product Builder examination",
        detail: "Lead proctor · 38 candidates · 14:00–16:00",
        status: "Live",
        owner: "Amina Mutesi",
        lane: "In session",
      },
      {
        name: "Responsible AI oral defence",
        detail: "Panel proctor · 12 candidates · tomorrow",
        status: "Ready",
        owner: "Amina Mutesi",
        lane: "Confirmed",
      },
      {
        name: "Agentic Workflow practical",
        detail: "Relief proctor · 24 candidates",
        status: "Preflight",
        owner: "Grace Namusoke",
        lane: "Upcoming",
      },
      {
        name: "Factual-note calibration",
        detail: "Required certification practice",
        status: "Due",
        owner: "Amina Mutesi",
        lane: "Training",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-command-proctor-workload-workload-board",
    model: "board",
    layoutGroup: "board",
  },
  "connection-health": {
    id: "connection-health",
    area: "command",
    areaLabel: "Proctor Command",
    title: "Connection Health",
    description: "Monitor Academy, Class and Labs handoff health, candidate connectivity and service status.",
    group: "Proctor Command",
    icon: "pulse",
    family: "service-health",
    source: "static",
    primary: { label: "Run connection diagnostic", intent: "action:test-connection", icon: "pulse" },
    secondary: [
      {
        label: "Open technical support",
        intent: "navigate:communications:technical-support",
        icon: "monitor",
      },
      {
        label: "View handoff settings",
        intent: "navigate:governance:connected-platform-settings",
        icon: "link",
      },
    ],
    metrics: [
      { label: "Academy API", value: "99.99%", trend: "Healthy", icon: "shield", tone: "good" },
      { label: "Chatoner Class", value: "99.97%", trend: "Media healthy", icon: "video", tone: "good" },
      {
        label: "Chatoner AI Labs",
        value: "99.95%",
        trend: "2 preflight checks",
        icon: "flask",
        tone: "warn",
      },
      { label: "Candidate median", value: "84 ms", trend: "Regional latency", icon: "pulse", tone: "info" },
    ],
    tabs: ["Platform services", "Candidate connectivity", "Handoff health"],
    columns: [
      { key: "name", label: "Service" },
      { key: "detail", label: "Operational dependency" },
      { key: "latency", label: "Observed latency" },
      { key: "status", label: "Health" },
      { key: "owner", label: "Owner" },
    ],
    rows: [
      {
        name: "Academy Secure Room",
        detail: "Presence, timers, controls and evidence service",
        status: "Healthy",
        owner: "Academy Operations",
        latency: "62 ms",
      },
      {
        name: "Chatoner Class media",
        detail: "Waiting room, captions, recordings and panel audio",
        status: "Healthy",
        owner: "Class Operations",
        latency: "74 ms",
      },
      {
        name: "Chatoner AI Labs runtime",
        detail: "Practical environments, compute and returned evidence",
        status: "Preflight",
        owner: "Labs Operations",
        latency: "91 ms",
      },
      {
        name: "Evidence service",
        detail: "Content-addressed sealing and custody ledger",
        status: "Healthy",
        owner: "Trust Operations",
        latency: "48 ms",
      },
      {
        name: "Uganda candidate region",
        detail: "38 active candidates · 2 reconnecting",
        status: "Monitor",
        owner: "Technical Support",
        latency: "118 ms",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-command-connection-health-service-health",
    model: "health",
    layoutGroup: "connection",
  },
  "scheduled-sessions": {
    id: "scheduled-sessions",
    area: "sessions",
    areaLabel: "Sessions & Assignments",
    title: "Scheduled Sessions",
    description: "All assigned and available assessment sessions across Academy, Class and Labs.",
    group: "Sessions",
    icon: "calendar",
    family: "session-catalogue",
    source: "sessions",
    primary: { label: "Open selected session", intent: "modal:session-detail", icon: "eye" },
    secondary: [
      { label: "Calendar view", intent: "navigate:sessions:proctor-calendar", icon: "calendar" },
      { label: "Export assignment list", intent: "export:sessions", icon: "download" },
    ],
    metrics: [
      { label: "Assigned", value: "6", trend: "Current proctor scope", icon: "clipboard", tone: "info" },
      { label: "Live", value: "1", trend: "Academy Secure Room", icon: "video", tone: "good" },
      { label: "Preflight", value: "2", trend: "Labs practicals", icon: "flask", tone: "warn" },
      { label: "Ready", value: "3", trend: "No blockers", icon: "check", tone: "violet" },
    ],
    tabs: ["Assigned", "Available relief", "Completed"],
    columns: [
      { key: "title", label: "Assessment session" },
      { key: "date", label: "Date" },
      { key: "time", label: "Time" },
      { key: "platform", label: "Platform" },
      { key: "candidates", label: "Candidates" },
      { key: "status", label: "Status" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-sessions-scheduled-sessions-session-catalogue",
    model: "cards",
    layoutGroup: "cards",
  },
  "my-assignments": {
    id: "my-assignments",
    area: "sessions",
    areaLabel: "Sessions & Assignments",
    title: "My Assignments",
    description: "Your confirmed lead, room, relief, accessibility and handover responsibilities.",
    group: "Sessions",
    icon: "clipboard",
    family: "assignment-matrix",
    source: "sessions",
    primary: { label: "Confirm assignment", intent: "action:advance", icon: "check" },
    secondary: [
      { label: "Open team coverage", intent: "navigate:sessions:proctor-teams", icon: "users" },
      { label: "Prepare handover", intent: "navigate:sessions:handover-notes", icon: "history" },
    ],
    metrics: [
      { label: "Confirmed", value: "5", trend: "Role accepted", icon: "check", tone: "good" },
      { label: "Awaiting confirmation", value: "1", trend: "Due today", icon: "clock", tone: "warn" },
      { label: "Lead proctor", value: "2", trend: "Expanded controls", icon: "crown", tone: "violet" },
      { label: "Relief duty", value: "3", trend: "Coverage windows", icon: "users", tone: "info" },
    ],
    tabs: ["Confirmed duties", "Awaiting response", "Relief coverage"],
    columns: [
      { key: "title", label: "Assignment" },
      { key: "role", label: "Assigned role" },
      { key: "date", label: "Date" },
      { key: "time", label: "Shift" },
      { key: "lead", label: "Lead" },
      { key: "status", label: "Confirmation" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-sessions-my-assignments-assignment-matrix",
    model: "matrix",
    layoutGroup: "board",
  },
  "session-details": {
    id: "session-details",
    area: "sessions",
    areaLabel: "Sessions & Assignments",
    title: "Session Details",
    description:
      "Assessment scope, candidates, approved materials, accommodations, timing and escalation contacts.",
    group: "Sessions",
    icon: "file",
    family: "session-brief",
    source: "sessions",
    primary: { label: "Open session record", intent: "modal:session-detail", icon: "file" },
    secondary: [
      { label: "Candidate roster", intent: "navigate:readiness:candidate-roster", icon: "users" },
      {
        label: "Approved accommodations",
        intent: "navigate:accommodations:approved-accommodations",
        icon: "accessibility",
      },
    ],
    metrics: [
      { label: "Candidates", value: "38", trend: "Assigned scope", icon: "users", tone: "info" },
      { label: "Ready", value: "33", trend: "5 checks outstanding", icon: "check", tone: "good" },
      {
        label: "Accommodations",
        value: "6",
        trend: "Approved instructions",
        icon: "accessibility",
        tone: "violet",
      },
      { label: "Escalation contacts", value: "5", trend: "Named and current", icon: "shield", tone: "warn" },
    ],
    tabs: ["Brief", "Candidates", "Materials", "Escalation"],
    columns: [
      { key: "title", label: "Assessment" },
      { key: "program", label: "Program" },
      { key: "location", label: "Location" },
      { key: "code", label: "Session code" },
      { key: "role", label: "Your role" },
      { key: "risk", label: "Risk context" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-sessions-session-details-session-brief",
    model: "brief",
    layoutGroup: "brief",
  },
  "proctor-calendar": {
    id: "proctor-calendar",
    area: "sessions",
    areaLabel: "Sessions & Assignments",
    title: "Proctor Calendar",
    description: "Assessment shifts, rehearsals, preflight windows, breaks, handovers and training.",
    group: "Scheduling",
    icon: "calendar",
    family: "calendar",
    source: "sessions",
    primary: { label: "Add operational event", intent: "modal:calendar", icon: "plus" },
    secondary: [
      { label: "Today’s schedule", intent: "navigate:command:todays-schedule", icon: "calendar" },
      { label: "Export calendar", intent: "export:calendar", icon: "download" },
    ],
    metrics: [
      { label: "October sessions", value: "12", trend: "8 assigned", icon: "calendar", tone: "info" },
      { label: "Preflights", value: "7", trend: "30 minutes each", icon: "check", tone: "good" },
      { label: "Training", value: "2", trend: "Certification current", icon: "book", tone: "violet" },
      { label: "Conflicts", value: "1", trend: "Relief overlap", icon: "alert", tone: "warn" },
    ],
    tabs: ["Month", "Week", "Agenda"],
    columns: [
      { key: "date", label: "Date" },
      { key: "time", label: "Time" },
      { key: "title", label: "Assessment" },
      { key: "platform", label: "Platform" },
      { key: "status", label: "Status" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-sessions-proctor-calendar-calendar",
    model: "calendar",
    layoutGroup: "timeline",
  },
  "proctor-teams": {
    id: "proctor-teams",
    area: "sessions",
    areaLabel: "Sessions & Assignments",
    title: "Proctor Teams",
    description:
      "Lead proctors, room proctors, relief staff, technical support, accessibility and safeguarding contacts.",
    group: "People",
    icon: "users",
    family: "team-directory",
    source: "static",
    primary: { label: "Open team directory", intent: "modal:proctor-team", icon: "users" },
    secondary: [
      { label: "Request relief", intent: "modal:proctor-team", icon: "plus" },
      { label: "Create handover", intent: "navigate:sessions:handover-notes", icon: "history" },
    ],
    metrics: [
      { label: "Assigned people", value: "8", trend: "Across active rooms", icon: "users", tone: "info" },
      { label: "Lead proctors", value: "3", trend: "Named authority", icon: "crown", tone: "violet" },
      { label: "Relief available", value: "2", trend: "Next 30 minutes", icon: "check", tone: "good" },
      {
        label: "Support roles",
        value: "4",
        trend: "Technical, access, safety",
        icon: "shield",
        tone: "warn",
      },
    ],
    tabs: ["Active team", "Relief roster", "Specialist contacts"],
    columns: [
      { key: "name", label: "Team member" },
      { key: "detail", label: "Role / session" },
      { key: "owner", label: "Coverage" },
      { key: "contact", label: "Channel" },
      { key: "status", label: "Availability" },
    ],
    rows: [
      {
        name: "Amina Mutesi",
        detail: "Lead Proctor · APB-7Q4M",
        status: "In room",
        owner: "Candidates 1–19",
        contact: "Secure channel",
      },
      {
        name: "Daniel Okello",
        detail: "Room Proctor · APB-7Q4M",
        status: "In room",
        owner: "Candidates 20–38",
        contact: "Secure channel",
      },
      {
        name: "Joel Ssempala",
        detail: "Relief Proctor · handover 15:00",
        status: "Ready",
        owner: "Full room",
        contact: "Secure channel",
      },
      {
        name: "Peter Ouma",
        detail: "Technical Support",
        status: "Active",
        owner: "2 cases",
        contact: "Support channel",
      },
      {
        name: "Grace Namusoke",
        detail: "Accessibility Liaison",
        status: "Available",
        owner: "6 plans",
        contact: "Protected channel",
      },
      {
        name: "Sarah Kato",
        detail: "Safeguarding Contact",
        status: "On call",
        owner: "Protected cases",
        contact: "Emergency directory",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-sessions-proctor-teams-team-directory",
    model: "directory",
    layoutGroup: "cards",
  },
  "handover-notes": {
    id: "handover-notes",
    area: "sessions",
    areaLabel: "Sessions & Assignments",
    title: "Handover Notes",
    description:
      "Structured, factual room handovers that preserve context without speculating about outcomes.",
    group: "Operations",
    icon: "message",
    family: "handover-ledger",
    source: "static",
    primary: { label: "Create factual handover", intent: "modal:handover", icon: "history" },
    secondary: [
      { label: "Team coverage", intent: "navigate:sessions:proctor-teams", icon: "users" },
      { label: "Open live room", intent: "navigate:rooms:active-exam-room", icon: "monitor" },
    ],
    metrics: [
      {
        label: "Open handovers",
        value: "3",
        trend: "Named receiving proctor",
        icon: "history",
        tone: "warn",
      },
      { label: "Completed today", value: "5", trend: "Acknowledged", icon: "check", tone: "good" },
      { label: "Unresolved actions", value: "4", trend: "Remain visible", icon: "alert", tone: "info" },
      {
        label: "Missing acknowledgement",
        value: "1",
        trend: "Due in 12 minutes",
        icon: "clock",
        tone: "danger",
      },
    ],
    tabs: ["Open handovers", "Acknowledged", "Archive"],
    columns: [
      { key: "name", label: "Handover" },
      { key: "detail", label: "Factual context" },
      { key: "time", label: "Effective time" },
      { key: "owner", label: "Receiving owner" },
      { key: "status", label: "Acknowledgement" },
    ],
    rows: [
      {
        name: "APB-7Q4M · Lead shift",
        detail: "Candidate 20–38 coverage and two technical events",
        status: "Awaiting acknowledgement",
        owner: "Joel Ssempala",
        time: "15:00 EAT",
      },
      {
        name: "LAB-AWF-8820 · Preflight",
        detail: "Runtime safety check and two candidate device exceptions",
        status: "Draft",
        owner: "Grace Namusoke",
        time: "12:30 EAT",
      },
      {
        name: "CLS-RAI-2841 · Panel",
        detail: "Caption readiness and panel turn order",
        status: "Acknowledged",
        owner: "Daniel Okello",
        time: "08:40 EAT",
      },
      {
        name: "Evidence export EXP-041",
        detail: "Transferred to Integrity Review; access expires in 24 hours",
        status: "Complete",
        owner: "Trust Operations",
        time: "Yesterday",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-sessions-handover-notes-handover-ledger",
    model: "timeline",
    layoutGroup: "timeline",
  },
  "readiness-dashboard": {
    id: "readiness-dashboard",
    area: "readiness",
    areaLabel: "Candidate Readiness",
    title: "Readiness Dashboard",
    description:
      "Identity, device, network, room, materials, accommodations and consent readiness at a glance.",
    group: "Readiness",
    icon: "pulse",
    family: "readiness-dashboard",
    source: "candidates",
    primary: { label: "Open readiness checklist", intent: "modal:readiness-checklist", icon: "check" },
    secondary: [
      { label: "Candidate roster", intent: "navigate:readiness:candidate-roster", icon: "users" },
      { label: "Late & re-entry queue", intent: "navigate:readiness:late-reentry-queue", icon: "history" },
    ],
    metrics: [
      { label: "Ready", value: "112", trend: "90.3%", icon: "check", tone: "good" },
      { label: "Identity pending", value: "5", trend: "Human verification", icon: "user", tone: "warn" },
      { label: "Technical blockers", value: "4", trend: "Support active", icon: "pulse", tone: "info" },
      {
        label: "Accommodation checks",
        value: "3",
        trend: "Protected plans",
        icon: "accessibility",
        tone: "violet",
      },
    ],
    tabs: ["Overview", "Blocking checks", "By session"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "candidateNo", label: "Candidate number" },
      { key: "identity", label: "Identity" },
      { key: "network", label: "Connection" },
      { key: "accommodation", label: "Accommodation" },
      { key: "status", label: "Readiness" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-readiness-readiness-dashboard-readiness-dashboard",
    model: "dashboard",
    layoutGroup: "dashboard",
  },
  "candidate-roster": {
    id: "candidate-roster",
    area: "readiness",
    areaLabel: "Candidate Readiness",
    title: "Candidate Roster",
    description: "Search and filter large candidate groups with scoped, session-specific information.",
    group: "Candidates",
    icon: "users",
    family: "candidate-roster",
    source: "candidates",
    primary: { label: "Open candidate record", intent: "modal:candidate-detail", icon: "user" },
    secondary: [
      { label: "Readiness dashboard", intent: "navigate:readiness:readiness-dashboard", icon: "check" },
      { label: "Export assigned roster", intent: "export:candidates", icon: "download" },
    ],
    metrics: [
      { label: "Assigned candidates", value: "124", trend: "Across 8 sessions", icon: "users", tone: "info" },
      { label: "Online", value: "117", trend: "Presence current", icon: "pulse", tone: "good" },
      { label: "Needs support", value: "7", trend: "Technical or access", icon: "help", tone: "warn" },
      { label: "Identity re-check", value: "4", trend: "Human procedure", icon: "user", tone: "violet" },
    ],
    tabs: ["All assigned", "Current session", "Needs support"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "candidateNo", label: "Number" },
      { key: "program", label: "Program" },
      { key: "identity", label: "Identity" },
      { key: "network", label: "Network" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-readiness-candidate-roster-candidate-roster",
    model: "roster",
    layoutGroup: "table",
  },
  "identity-verification": {
    id: "identity-verification",
    area: "readiness",
    areaLabel: "Candidate Readiness",
    title: "Identity Verification",
    description: "Human verification workflow, approved alternatives and documented re-checks.",
    group: "Readiness Checks",
    icon: "user",
    family: "identity-workbench",
    source: "candidates",
    primary: { label: "Record human verification", intent: "modal:readiness-checklist", icon: "user" },
    secondary: [
      {
        label: "Alternative verification",
        intent: "navigate:accommodations:alternative-verification",
        icon: "accessibility",
      },
      { label: "Record factual issue", intent: "modal:record-incident", icon: "alert" },
    ],
    metrics: [
      { label: "Verified", value: "112", trend: "Approved human procedure", icon: "check", tone: "good" },
      {
        label: "Re-check required",
        value: "4",
        trend: "Image or document quality",
        icon: "user",
        tone: "warn",
      },
      {
        label: "Alternative path",
        value: "3",
        trend: "Accessibility approved",
        icon: "accessibility",
        tone: "violet",
      },
      { label: "Unverified", value: "1", trend: "Do not admit automatically", icon: "lock", tone: "danger" },
    ],
    tabs: ["Verification queue", "Alternative paths", "Audit"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "candidateNo", label: "Candidate number" },
      { key: "identity", label: "Verification state" },
      { key: "language", label: "Language" },
      { key: "lastEvent", label: "Latest factual event" },
      { key: "status", label: "Session state" },
    ],
    rows: [],
    notes: [
      {
        title: "No autonomous biometric decision",
        body: "The proctor performs the approved human verification or uses the approved accessible alternative.",
        icon: "user",
      },
      {
        title: "Separate admission from academic outcome",
        body: "Verification controls session entry only; it does not assess candidate work.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-readiness-identity-verification-identity-workbench",
    model: "verification",
    layoutGroup: "checklist",
  },
  "device-network-checks": {
    id: "device-network-checks",
    area: "readiness",
    areaLabel: "Candidate Readiness",
    title: "Device & Network Checks",
    description: "Camera, microphone, browser, bandwidth, power and fallback readiness.",
    group: "Readiness Checks",
    icon: "monitor",
    family: "device-diagnostics",
    source: "candidates",
    primary: { label: "Run assigned device check", intent: "action:test-device", icon: "pulse" },
    secondary: [
      { label: "Technical support", intent: "navigate:communications:technical-support", icon: "monitor" },
      { label: "Device logs", intent: "navigate:evidence:device-network-logs", icon: "database" },
    ],
    metrics: [
      { label: "Browser ready", value: "119", trend: "5 need action", icon: "monitor", tone: "good" },
      { label: "Camera ready", value: "116", trend: "Published policy only", icon: "camera", tone: "info" },
      { label: "Microphone ready", value: "118", trend: "Oral exams included", icon: "mic", tone: "violet" },
      { label: "Network below target", value: "6", trend: "Fallback available", icon: "pulse", tone: "warn" },
    ],
    tabs: ["Device status", "Network quality", "Fallback readiness"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "network", label: "Network" },
      { key: "camera", label: "Camera" },
      { key: "microphone", label: "Microphone" },
      { key: "lastEvent", label: "Latest event" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-readiness-device-network-checks-device-diagnostics",
    model: "diagnostics",
    layoutGroup: "diagnostics",
  },
  "room-environment-checks": {
    id: "room-environment-checks",
    area: "readiness",
    areaLabel: "Candidate Readiness",
    title: "Room & Environment Checks",
    description: "Approved environment checks using proportionate, documented procedures.",
    group: "Readiness Checks",
    icon: "eye",
    family: "environment-checks",
    source: "candidates",
    primary: { label: "Record environment check", intent: "modal:readiness-checklist", icon: "eye" },
    secondary: [
      { label: "Allowed materials", intent: "navigate:readiness:allowed-materials", icon: "file" },
      { label: "Escalation guidance", intent: "navigate:incidents:escalations", icon: "alert" },
    ],
    metrics: [
      { label: "Complete", value: "109", trend: "Published checks only", icon: "check", tone: "good" },
      { label: "Needs re-check", value: "8", trend: "Candidate informed", icon: "eye", tone: "warn" },
      { label: "Alternative room", value: "4", trend: "Institution approved", icon: "home", tone: "info" },
      {
        label: "Privacy-limited",
        value: "124",
        trend: "No broader surveillance",
        icon: "lock",
        tone: "violet",
      },
    ],
    tabs: ["Current session", "Re-check queue", "Approved alternatives"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "candidateNo", label: "Number" },
      { key: "lastEvent", label: "Observed environment event" },
      { key: "accommodation", label: "Approved adjustment" },
      { key: "status", label: "Check state" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-readiness-room-environment-checks-environment-checks",
    model: "checklist",
    layoutGroup: "checklist",
  },
  "allowed-materials": {
    id: "allowed-materials",
    area: "readiness",
    areaLabel: "Candidate Readiness",
    title: "Allowed Materials",
    description: "Exam-specific reference sheets, calculators, tools, files and accessibility aids.",
    group: "Assessment Rules",
    icon: "book",
    family: "materials-register",
    source: "static",
    primary: { label: "Confirm allowed materials", intent: "action:advance", icon: "check" },
    secondary: [
      { label: "Session details", intent: "navigate:sessions:session-details", icon: "file" },
      { label: "Record material issue", intent: "modal:record-incident", icon: "alert" },
    ],
    metrics: [
      { label: "Confirmed", value: "118", trend: "Exam-specific declaration", icon: "check", tone: "good" },
      {
        label: "Clarifications",
        value: "4",
        trend: "Assessor response pending",
        icon: "message",
        tone: "warn",
      },
      { label: "Restricted items", value: "7", trend: "Published before entry", icon: "lock", tone: "info" },
      {
        label: "Accessibility aids",
        value: "12",
        trend: "Approved and protected",
        icon: "accessibility",
        tone: "violet",
      },
    ],
    tabs: ["Allowed register", "Candidate confirmations", "Clarifications"],
    columns: [
      { key: "name", label: "Material / aid" },
      { key: "detail", label: "Rule" },
      { key: "scope", label: "Applies to" },
      { key: "owner", label: "Authority" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        name: "Scientific calculator",
        detail: "Data & Predictive AI practical · non-programmable",
        status: "Allowed",
        owner: "Assessment Design",
        scope: "All candidates",
      },
      {
        name: "Prompt-reference sheet",
        detail: "One page · Academy-issued version 3",
        status: "Allowed",
        owner: "Assessment Design",
        scope: "AI Product Builder",
      },
      {
        name: "Screen reader",
        detail: "Approved assistive technology; no additional restriction",
        status: "Approved aid",
        owner: "Accessibility Office",
        scope: "Named candidates",
      },
      {
        name: "External generative AI",
        detail: "Not available unless the intended Lab exercise explicitly provides it",
        status: "Restricted",
        owner: "Assessment Policy",
        scope: "All sessions",
      },
      {
        name: "Personal notes",
        detail: "Not allowed in the secure applied examination",
        status: "Restricted",
        owner: "Assessment Policy",
        scope: "APB-7Q4M",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-readiness-allowed-materials-materials-register",
    model: "register",
    layoutGroup: "table",
  },
  "late-reentry-queue": {
    id: "late-reentry-queue",
    area: "readiness",
    areaLabel: "Candidate Readiness",
    title: "Late & Re-entry Queue",
    description: "Late arrivals, approved breaks, reconnects and re-entry decisions under policy.",
    group: "Exceptions",
    icon: "history",
    family: "arrival-reentry-queue",
    source: "candidates",
    primary: { label: "Open re-entry procedure", intent: "modal:break-action", icon: "history" },
    secondary: [
      { label: "Secure room controls", intent: "navigate:rooms:breaks-reentry", icon: "sliders" },
      { label: "Technical incidents", intent: "navigate:incidents:technical-incidents", icon: "pulse" },
    ],
    metrics: [
      { label: "Late arrivals", value: "3", trend: "Awaiting approved entry", icon: "clock", tone: "warn" },
      { label: "Reconnecting", value: "2", trend: "Work and timer preserved", icon: "pulse", tone: "info" },
      { label: "Approved re-entry", value: "5", trend: "Human check complete", icon: "check", tone: "good" },
      {
        label: "Denied by policy",
        value: "1",
        trend: "Referred to assessment authority",
        icon: "lock",
        tone: "danger",
      },
    ],
    tabs: ["Late arrival", "Reconnect", "Approved re-entry", "Transferred"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "candidateNo", label: "Number" },
      { key: "lastEvent", label: "Arrival / connection event" },
      { key: "timeRemaining", label: "Timer state" },
      { key: "status", label: "Queue state" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-readiness-late-reentry-queue-arrival-reentry-queue",
    model: "queue",
    layoutGroup: "queue",
  },
  "active-exam-room": {
    id: "active-exam-room",
    area: "rooms",
    areaLabel: "Secure Exam Rooms",
    title: "Active Exam Room",
    description:
      "Monitor the current room, candidate states, timing, signals, messages and human interventions.",
    group: "Secure Rooms",
    icon: "monitor",
    family: "live-room",
    source: "candidates",
    primary: { label: "Open room", intent: "navigate:rooms:active-exam-room", icon: "monitor" },
    secondary: [
      { label: "Export current view", intent: "export:view", icon: "download" },
      { label: "Open role boundary", intent: "modal:scope-boundary", icon: "shield" },
    ],
    metrics: [
      { label: "Candidates", value: "38", trend: "Current room", icon: "users", tone: "info" },
      { label: "Normal", value: "29", trend: "No action needed", icon: "check", tone: "good" },
      { label: "Needs review", value: "4", trend: "Human attention", icon: "eye", tone: "warn" },
      { label: "Approved breaks", value: "2", trend: "Timers protected", icon: "clock", tone: "violet" },
    ],
    tabs: ["Candidate grid", "Priority timeline", "Controls"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-rooms-active-exam-room-live-room",
    model: "special",
    layoutGroup: "live",
  },
  "room-overview": {
    id: "room-overview",
    area: "rooms",
    areaLabel: "Secure Exam Rooms",
    title: "Room Overview",
    description: "Session rules, platform, timing, proctor team, candidates and active controls.",
    group: "Secure Rooms",
    icon: "layout",
    family: "room-overview",
    source: "static",
    primary: { label: "Open candidate grid", intent: "navigate:rooms:candidate-grid", icon: "grid" },
    secondary: [
      { label: "Secure messages", intent: "navigate:rooms:secure-messages", icon: "message" },
      { label: "Session controls", intent: "navigate:rooms:session-controls", icon: "sliders" },
    ],
    metrics: [
      { label: "Room elapsed", value: "01:12:43", trend: "Session stable", icon: "clock", tone: "info" },
      { label: "Candidates present", value: "36", trend: "2 approved breaks", icon: "users", tone: "good" },
      {
        label: "Open support cases",
        value: "5",
        trend: "Technical/accessibility",
        icon: "help",
        tone: "warn",
      },
      { label: "Evidence events", value: "18", trend: "Minimum necessary", icon: "database", tone: "violet" },
    ],
    tabs: ["Overview", "Team coverage", "Service status"],
    columns: [
      { key: "name", label: "Room function" },
      { key: "detail", label: "Current state" },
      { key: "owner", label: "Operational owner" },
      { key: "status", label: "Health" },
    ],
    rows: [
      {
        name: "Candidate presence",
        detail: "36 active · 2 approved breaks · 0 unexplained absence",
        status: "Stable",
        owner: "Lead Proctor",
      },
      {
        name: "Timer service",
        detail: "38 synchronized timers · accommodation adjustments applied",
        status: "Healthy",
        owner: "Academy Platform",
      },
      {
        name: "Secure messages",
        detail: "7 candidate requests · 5 resolved · 2 assigned",
        status: "Monitor",
        owner: "Room Proctor",
      },
      {
        name: "Evidence service",
        detail: "18 events sealed · 0 integrity failures",
        status: "Healthy",
        owner: "Evidence Service",
      },
      {
        name: "Relief handover",
        detail: "Joel Ssempala receives lead coverage at 15:00 EAT",
        status: "Due",
        owner: "Amina Mutesi",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-rooms-room-overview-room-overview",
    model: "dashboard",
    layoutGroup: "brief",
  },
  "candidate-grid": {
    id: "candidate-grid",
    area: "rooms",
    areaLabel: "Secure Exam Rooms",
    title: "Candidate Grid",
    description:
      "Large, searchable and paginated candidate view with status, connectivity and accommodations.",
    group: "Secure Rooms",
    icon: "grid",
    family: "candidate-grid",
    source: "candidates",
    primary: { label: "Open candidate detail", intent: "modal:candidate-detail", icon: "user" },
    secondary: [
      { label: "Room overview", intent: "navigate:rooms:room-overview", icon: "monitor" },
      { label: "Priority signals", intent: "navigate:command:priority-signals", icon: "alert" },
    ],
    metrics: [
      { label: "Visible page", value: "24", trend: "Large-group pagination", icon: "grid", tone: "info" },
      { label: "Online", value: "36", trend: "Current room", icon: "pulse", tone: "good" },
      { label: "Human attention", value: "4", trend: "No automatic finding", icon: "eye", tone: "warn" },
      { label: "Support active", value: "3", trend: "Technical/accessibility", icon: "help", tone: "violet" },
    ],
    tabs: ["All candidates", "Attention queue", "Approved breaks"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "candidateNo", label: "Number" },
      { key: "network", label: "Network" },
      { key: "camera", label: "Camera" },
      { key: "timeRemaining", label: "Remaining" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-rooms-candidate-grid-candidate-grid",
    model: "grid",
    layoutGroup: "grid",
  },
  "secure-messages": {
    id: "secure-messages",
    area: "rooms",
    areaLabel: "Secure Exam Rooms",
    title: "Secure Messages",
    description:
      "Candidate, proctor-team, assessor, technical and safeguarding communications with audit context.",
    group: "Communication",
    icon: "message",
    family: "secure-messages",
    source: "static",
    primary: { label: "Send secure message", intent: "modal:candidate-message", icon: "send" },
    secondary: [
      { label: "Export current view", intent: "export:view", icon: "download" },
      { label: "Open role boundary", intent: "modal:scope-boundary", icon: "shield" },
    ],
    metrics: [
      { label: "Open requests", value: "7", trend: "2 unread", icon: "message", tone: "warn" },
      { label: "Resolved today", value: "31", trend: "Factual support", icon: "check", tone: "good" },
      { label: "Broadcasts", value: "2", trend: "Session-wide", icon: "send", tone: "info" },
      { label: "Escalated", value: "3", trend: "Named support owner", icon: "alert", tone: "violet" },
    ],
    tabs: ["Candidate help", "Proctor team", "Broadcasts"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-rooms-secure-messages-secure-messages",
    model: "special",
    layoutGroup: "messages",
  },
  "session-controls": {
    id: "session-controls",
    area: "rooms",
    areaLabel: "Secure Exam Rooms",
    title: "Session Controls",
    description:
      "Policy-authorized pause, resume, time extension, room broadcast, lock and emergency controls.",
    group: "Operations",
    icon: "sliders",
    family: "session-controls",
    source: "static",
    primary: { label: "Open controlled action", intent: "modal:secure-note", icon: "sliders" },
    secondary: [
      { label: "Export current view", intent: "export:view", icon: "download" },
      { label: "Open role boundary", intent: "modal:scope-boundary", icon: "shield" },
    ],
    metrics: [
      { label: "Session state", value: "Running", trend: "Version 42", icon: "monitor", tone: "good" },
      { label: "Active timers", value: "36", trend: "2 approved pauses", icon: "clock", tone: "info" },
      { label: "Control locks", value: "0", trend: "No conflicting action", icon: "lock", tone: "good" },
      {
        label: "Pending authority",
        value: "1",
        trend: "Room-wide pause request",
        icon: "shield",
        tone: "warn",
      },
    ],
    tabs: ["Controls", "Authority history", "Service locks"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-rooms-session-controls-session-controls",
    model: "special",
    layoutGroup: "controls",
  },
  "breaks-reentry": {
    id: "breaks-reentry",
    area: "rooms",
    areaLabel: "Secure Exam Rooms",
    title: "Breaks & Re-entry",
    description: "Approved breaks, accessibility pauses, reconnects and identity re-checks.",
    group: "Operations",
    icon: "history",
    family: "breaks-reentry",
    source: "static",
    primary: { label: "Apply approved break", intent: "modal:break-action", icon: "clock" },
    secondary: [
      { label: "Export current view", intent: "export:view", icon: "download" },
      { label: "Open role boundary", intent: "modal:scope-boundary", icon: "shield" },
    ],
    metrics: [
      { label: "Approved breaks", value: "2", trend: "Current room", icon: "clock", tone: "violet" },
      {
        label: "Medical plans",
        value: "1",
        trend: "Instructions applied",
        icon: "accessibility",
        tone: "info",
      },
      { label: "Re-entry checks", value: "3", trend: "Human complete", icon: "check", tone: "good" },
      { label: "Timer adjustments", value: "4", trend: "Server recorded", icon: "history", tone: "warn" },
    ],
    tabs: ["Active breaks", "Re-entry queue", "Timer history"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-rooms-breaks-reentry-breaks-reentry",
    model: "special",
    layoutGroup: "timeline",
  },
  "emergency-procedures": {
    id: "emergency-procedures",
    area: "rooms",
    areaLabel: "Secure Exam Rooms",
    title: "Emergency Procedures",
    description: "Technical outage, safety, wellbeing, evacuation, safeguarding and continuity playbooks.",
    group: "Safety",
    icon: "alert",
    family: "emergency-runbook",
    source: "static",
    primary: { label: "Open emergency action", intent: "modal:emergency", icon: "alert" },
    secondary: [
      { label: "Export current view", intent: "export:view", icon: "download" },
      { label: "Open role boundary", intent: "modal:scope-boundary", icon: "shield" },
    ],
    metrics: [
      {
        label: "Current room state",
        value: "Normal",
        trend: "No emergency active",
        icon: "shield",
        tone: "good",
      },
      { label: "Relief contacts", value: "6", trend: "Current and tested", icon: "users", tone: "info" },
      { label: "Continuity paths", value: "4", trend: "Platform-specific", icon: "history", tone: "violet" },
      { label: "Last rehearsal", value: "12 days", trend: "Passed", icon: "check", tone: "warn" },
    ],
    tabs: ["Immediate safety", "Service outage", "Evacuation", "Protected support"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-rooms-emergency-procedures-emergency-runbook",
    model: "special",
    layoutGroup: "brief",
  },
  "class-connection": {
    id: "class-connection",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Class Connection",
    description: "Independent class.chatoner.com connection for live, oral, panel and observed assessments.",
    group: "Chatoner Class",
    icon: "link",
    family: "class-handoff",
    source: "sessions",
    primary: { label: "Open assigned Class session", intent: "class:first", icon: "external" },
    secondary: [
      { label: "Class sessions", intent: "navigate:class:class-exam-sessions", icon: "video" },
      { label: "Connection policy", intent: "navigate:class:class-exam-policies", icon: "shield" },
    ],
    metrics: [
      {
        label: "Assigned Class sessions",
        value: "3",
        trend: "Independent media platform",
        icon: "video",
        tone: "info",
      },
      { label: "Ready to enter", value: "2", trend: "One-time handoff", icon: "check", tone: "good" },
      { label: "Caption plans", value: "4", trend: "Approved", icon: "message", tone: "violet" },
      { label: "Recording scopes", value: "3", trend: "Session-specific", icon: "lock", tone: "warn" },
    ],
    tabs: ["Assigned session", "Handoff context", "Trust boundary"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-class-class-connection-class-handoff",
    model: "special",
    layoutGroup: "connection",
  },
  "oral-live-exams": {
    id: "oral-live-exams",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Oral & Live Exams",
    description: "Scheduled oral, presentation, defence, interview and live practical assessments.",
    group: "Chatoner Class",
    icon: "video",
    family: "oral-exam-panel",
    source: "sessions",
    primary: { label: "Open oral exam room", intent: "class:oral", icon: "video" },
    secondary: [
      { label: "Panel sessions", intent: "navigate:class:panel-huddle-sessions", icon: "users" },
      { label: "Attendance", intent: "navigate:class:attendance-presence", icon: "check" },
    ],
    metrics: [
      { label: "Candidates today", value: "12", trend: "Oral defence panel", icon: "users", tone: "info" },
      { label: "Waiting room", value: "3", trend: "Identity complete", icon: "clock", tone: "warn" },
      { label: "In examination", value: "1", trend: "Panel live", icon: "video", tone: "good" },
      { label: "Completed", value: "8", trend: "Recording scoped", icon: "check", tone: "violet" },
    ],
    tabs: ["Turn queue", "Live candidate", "Panel notes"],
    columns: [
      { key: "title", label: "Oral assessment" },
      { key: "date", label: "Date" },
      { key: "time", label: "Window" },
      { key: "candidates", label: "Candidates" },
      { key: "lead", label: "Panel lead" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-class-oral-live-exams-oral-exam-panel",
    model: "panel",
    layoutGroup: "live",
  },
  "class-exam-sessions": {
    id: "class-exam-sessions",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Class Exam Sessions",
    description: "Session codes, invitation links, candidate scope, proctor assignment and return context.",
    group: "Chatoner Class",
    icon: "calendar",
    family: "class-session-list",
    source: "sessions",
    primary: { label: "Prepare Class handoff", intent: "class:first", icon: "link" },
    secondary: [
      { label: "Schedule", intent: "navigate:sessions:proctor-calendar", icon: "calendar" },
      { label: "Recordings", intent: "navigate:class:recordings-captions", icon: "play" },
    ],
    metrics: [
      { label: "Scheduled", value: "3", trend: "Current assignment", icon: "calendar", tone: "info" },
      { label: "Live", value: "1", trend: "Panel active", icon: "video", tone: "good" },
      { label: "Rehearsals", value: "2", trend: "Device/caption check", icon: "check", tone: "violet" },
      { label: "Missing confirmation", value: "1", trend: "Panel member", icon: "alert", tone: "warn" },
    ],
    tabs: ["Upcoming", "Live", "Completed"],
    columns: [
      { key: "title", label: "Class assessment" },
      { key: "code", label: "Class code" },
      { key: "time", label: "Time" },
      { key: "role", label: "Your role" },
      { key: "recording", label: "Recording policy" },
      { key: "status", label: "Status" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-class-class-exam-sessions-class-session-list",
    model: "cards",
    layoutGroup: "table",
  },
  "panel-huddle-sessions": {
    id: "panel-huddle-sessions",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Panel & Huddle Sessions",
    description: "Panel rooms, private deliberation huddles and authorized candidate waiting rooms.",
    group: "Chatoner Class",
    icon: "users",
    family: "panel-huddle-board",
    source: "static",
    primary: { label: "Start panel huddle", intent: "class:huddle", icon: "video" },
    secondary: [
      { label: "Oral exam queue", intent: "navigate:class:oral-live-exams", icon: "users" },
      {
        label: "Secure liaison",
        intent: "navigate:communications:instructor-assessor-liaison",
        icon: "message",
      },
    ],
    metrics: [
      { label: "Panel members", value: "5", trend: "All confirmed", icon: "users", tone: "good" },
      { label: "Candidate slots", value: "12", trend: "25 minutes each", icon: "clock", tone: "info" },
      { label: "Private huddles", value: "2", trend: "Before/after session", icon: "lock", tone: "violet" },
      { label: "Conflicts", value: "0", trend: "Segregation maintained", icon: "shield", tone: "good" },
    ],
    tabs: ["Panel roster", "Candidate turns", "Huddle agenda"],
    columns: [
      { key: "name", label: "Panel member" },
      { key: "detail", label: "Authority / role" },
      { key: "owner", label: "Scope" },
      { key: "time", label: "Window" },
      { key: "status", label: "Confirmation" },
    ],
    rows: [
      {
        name: "Dr. N. Wekesa",
        detail: "Assessment chair · final rubric authority",
        status: "Confirmed",
        owner: "Panel 1",
        time: "09:00–12:30",
      },
      {
        name: "Amina Mutesi",
        detail: "Panel proctor · procedure and continuity",
        status: "Confirmed",
        owner: "All candidates",
        time: "08:40–12:45",
      },
      {
        name: "S. Kaggwa",
        detail: "Assessor · responsible AI practice",
        status: "Confirmed",
        owner: "Candidates 1–6",
        time: "09:00–10:45",
      },
      {
        name: "J. Akello",
        detail: "Assessor · responsible AI practice",
        status: "Confirmed",
        owner: "Candidates 7–12",
        time: "10:45–12:30",
      },
      {
        name: "Mariam N.",
        detail: "Captioner · approved support role",
        status: "Ready",
        owner: "Named candidates",
        time: "08:45–12:45",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-class-panel-huddle-sessions-panel-huddle-board",
    model: "directory",
    layoutGroup: "board",
  },
  "attendance-presence": {
    id: "attendance-presence",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Attendance & Presence",
    description: "Join, leave, late, reconnect and presence records for the assessment session.",
    group: "Chatoner Class",
    icon: "check",
    family: "attendance-monitor",
    source: "candidates",
    primary: { label: "Record presence exception", intent: "modal:secure-note", icon: "check" },
    secondary: [
      { label: "Late & re-entry", intent: "navigate:readiness:late-reentry-queue", icon: "history" },
      { label: "Class connection", intent: "navigate:class:class-connection", icon: "link" },
    ],
    metrics: [
      { label: "Expected", value: "12", trend: "Oral defence", icon: "users", tone: "info" },
      { label: "Present", value: "11", trend: "Verified in Class", icon: "check", tone: "good" },
      { label: "Waiting room", value: "1", trend: "Approved late arrival", icon: "clock", tone: "warn" },
      {
        label: "Presence events",
        value: "46",
        trend: "Platform-returned metadata",
        icon: "pulse",
        tone: "violet",
      },
    ],
    tabs: ["Current session", "Late / absent", "Presence history"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "candidateNo", label: "Number" },
      { key: "online", label: "Present" },
      { key: "identity", label: "Admission check" },
      { key: "lastEvent", label: "Latest presence event" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-class-attendance-presence-attendance-monitor",
    model: "roster",
    layoutGroup: "grid",
  },
  "recordings-captions": {
    id: "recordings-captions",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Recordings & Captions",
    description: "Authorized recordings, captions, interpretation, transcripts, access and retention.",
    group: "Chatoner Class",
    icon: "play",
    family: "recording-library",
    source: "static",
    primary: { label: "Open authorized recording", intent: "modal:secure-note", icon: "play" },
    secondary: [
      {
        label: "Caption readiness",
        intent: "navigate:accommodations:captions-interpretation",
        icon: "message",
      },
      { label: "Retention rules", intent: "navigate:evidence:retention-access", icon: "clock" },
    ],
    metrics: [
      { label: "Authorized recordings", value: "4", trend: "Session-scoped", icon: "play", tone: "info" },
      { label: "Captions complete", value: "4", trend: "Original retained", icon: "message", tone: "good" },
      { label: "Restricted transcripts", value: "2", trend: "Panel only", icon: "lock", tone: "violet" },
      { label: "Deletion due", value: "1", trend: "In 12 days", icon: "clock", tone: "warn" },
    ],
    tabs: ["Recordings", "Captions & transcripts", "Access history"],
    columns: [
      { key: "name", label: "Media record" },
      { key: "detail", label: "Captions / scope" },
      { key: "retention", label: "Retention" },
      { key: "owner", label: "Authorized owner" },
      { key: "status", label: "Access" },
    ],
    rows: [
      {
        name: "Responsible AI oral defence · Panel 1",
        detail: "1h 42m · captions EN + LG · original audio retained",
        status: "Available",
        owner: "Assessment Panel",
        retention: "180 days",
      },
      {
        name: "Oral assessment rehearsal",
        detail: "24m · device and caption readiness only",
        status: "Restricted",
        owner: "Class Operations",
        retention: "30 days",
      },
      {
        name: "Panel huddle · pre-session",
        detail: "12m · protected discussion; not candidate evidence",
        status: "Restricted",
        owner: "Assessment Chair",
        retention: "90 days",
      },
      {
        name: "Candidate statement capture",
        detail: "8m · candidate-approved language and translation",
        status: "Sealed",
        owner: "Integrity Review",
        retention: "Case policy",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-class-recordings-captions-recording-library",
    model: "media",
    layoutGroup: "cards",
  },
  "class-exam-policies": {
    id: "class-exam-policies",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Class Exam Policies",
    description: "Waiting-room, recording, identity, captions, language, safeguarding and evidence rules.",
    group: "Governance",
    icon: "shield",
    family: "class-policy-matrix",
    source: "static",
    primary: { label: "Acknowledge Class policy", intent: "modal:policy-confirmation", icon: "check" },
    secondary: [
      { label: "Authority boundaries", intent: "navigate:governance:authority-boundaries", icon: "shield" },
      { label: "Privacy controls", intent: "navigate:governance:privacy-data-minimization", icon: "lock" },
    ],
    metrics: [
      { label: "Policies current", value: "9", trend: "Versioned", icon: "book", tone: "good" },
      { label: "Acknowledged", value: "8", trend: "One due", icon: "check", tone: "warn" },
      { label: "Recording profiles", value: "3", trend: "Session-specific", icon: "video", tone: "info" },
      {
        label: "Language profiles",
        value: "6",
        trend: "Candidate-controlled",
        icon: "globe",
        tone: "violet",
      },
    ],
    tabs: ["Admission & waiting room", "Recording", "Captions & language", "Panel authority"],
    columns: [
      { key: "name", label: "Policy" },
      { key: "detail", label: "Operational rule" },
      { key: "version", label: "Version" },
      { key: "owner", label: "Authority" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Waiting-room admission",
        detail: "Human verification and assignment scope required before entry",
        status: "Current",
        owner: "Class Operations",
        version: "v4.2",
      },
      {
        name: "Recording authority",
        detail: "Only the approved assessment recording profile may be used",
        status: "Current",
        owner: "Assessment Operations",
        version: "v3.8",
      },
      {
        name: "Caption and interpretation",
        detail: "Candidate-approved services; original content retained separately",
        status: "Current",
        owner: "Accessibility Office",
        version: "v2.9",
      },
      {
        name: "Private panel huddle",
        detail: "Separated from candidate media and evidence unless formally referenced",
        status: "Current",
        owner: "Assessment Chair",
        version: "v2.4",
      },
      {
        name: "Proctor media access",
        detail: "Session-scoped; no unrelated classes or recordings",
        status: "Acknowledgement due",
        owner: "Amina Mutesi",
        version: "v5.0",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-class-class-exam-policies-class-policy-matrix",
    model: "policy",
    layoutGroup: "policy",
  },
  "labs-connection": {
    id: "labs-connection",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Labs Connection",
    description: "Independent labs.chatoner.com connection for observed practical and technical assessments.",
    group: "Chatoner AI Labs",
    icon: "link",
    family: "labs-handoff",
    source: "sessions",
    primary: { label: "Open assigned Labs control", intent: "lab:first", icon: "external" },
    secondary: [
      { label: "Practical sessions", intent: "navigate:labs:lab-assessment-sessions", icon: "flask" },
      { label: "Labs boundaries", intent: "navigate:labs:labs-data-boundaries", icon: "lock" },
    ],
    metrics: [
      {
        label: "Assigned practicals",
        value: "3",
        trend: "Independent runtime platform",
        icon: "flask",
        tone: "info",
      },
      { label: "Preflight ready", value: "2", trend: "One safety check due", icon: "check", tone: "warn" },
      { label: "Active runtimes", value: "24", trend: "Candidate-scoped", icon: "pulse", tone: "good" },
      {
        label: "Evidence returns",
        value: "18",
        trend: "Metadata + sealed refs",
        icon: "database",
        tone: "violet",
      },
    ],
    tabs: ["Assigned practical", "Handoff claims", "Platform boundary"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-labs-labs-connection-labs-handoff",
    model: "special",
    layoutGroup: "connection",
  },
  "lab-assessment-sessions": {
    id: "lab-assessment-sessions",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Lab Assessment Sessions",
    description:
      "Assigned practical exam sessions, Lab environments, candidates, entitlements and proctor scope.",
    group: "Chatoner AI Labs",
    icon: "calendar",
    family: "lab-session-list",
    source: "sessions",
    primary: { label: "Open practical preflight", intent: "lab:first", icon: "flask" },
    secondary: [
      { label: "Intended environments", intent: "navigate:labs:intended-lab-environments", icon: "grid" },
      { label: "Runtime signals", intent: "navigate:labs:runtime-safety-signals", icon: "pulse" },
    ],
    metrics: [
      { label: "Scheduled", value: "3", trend: "Current proctor scope", icon: "calendar", tone: "info" },
      { label: "Preflight", value: "2", trend: "Runtime checks", icon: "check", tone: "warn" },
      { label: "Active", value: "1", trend: "24 candidates", icon: "flask", tone: "good" },
      { label: "Blocked", value: "0", trend: "No unauthorized environment", icon: "lock", tone: "good" },
    ],
    tabs: ["Scheduled", "Preflight", "Active", "Completed"],
    columns: [
      { key: "title", label: "Practical assessment" },
      { key: "code", label: "Session code" },
      { key: "program", label: "Intended Lab" },
      { key: "candidates", label: "Candidates" },
      { key: "risk", label: "Runtime context" },
      { key: "status", label: "Status" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-labs-lab-assessment-sessions-lab-session-list",
    model: "cards",
    layoutGroup: "cards",
  },
  "intended-lab-environments": {
    id: "intended-lab-environments",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Intended Lab Environments",
    description:
      "Exact environments connected to each practical assessment without reproducing the Labs runtime.",
    group: "Chatoner AI Labs",
    icon: "flask",
    family: "lab-environment-catalog",
    source: "static",
    primary: { label: "Inspect intended environment", intent: "modal:secure-note", icon: "eye" },
    secondary: [
      { label: "Labs connection", intent: "navigate:labs:labs-connection", icon: "link" },
      { label: "Data boundaries", intent: "navigate:labs:labs-data-boundaries", icon: "lock" },
    ],
    metrics: [
      { label: "Approved environments", value: "6", trend: "Entitlement-bound", icon: "grid", tone: "good" },
      { label: "Tool profiles", value: "14", trend: "Assessment-specific", icon: "sliders", tone: "info" },
      { label: "Internet profiles", value: "3", trend: "Off by default", icon: "globe", tone: "violet" },
      {
        label: "Environment mismatch",
        value: "1",
        trend: "Blocked at preflight",
        icon: "alert",
        tone: "warn",
      },
    ],
    tabs: ["Environment catalogue", "Tool profiles", "Access boundary"],
    columns: [
      { key: "name", label: "Intended environment" },
      { key: "detail", label: "Approved capability" },
      { key: "destination", label: "Labs destination" },
      { key: "owner", label: "Platform owner" },
      { key: "status", label: "Readiness" },
    ],
    rows: [
      {
        name: "Agentic Workflow Exam Runtime",
        detail: "Workflow canvas, approved tools, fixtures and evaluation harness",
        status: "Ready",
        owner: "Labs Operations",
        destination: "exam-room",
      },
      {
        name: "Data & Predictive AI Practical",
        detail: "Notebook, approved dataset, baseline model and explainability view",
        status: "Ready",
        owner: "Labs Operations",
        destination: "exam-room",
      },
      {
        name: "AI-App Builder Examination",
        detail: "Isolated repository, live preview, tests and evidence capture",
        status: "Ready",
        owner: "Labs Operations",
        destination: "exam-room",
      },
      {
        name: "Deep Learning Advanced Runtime",
        detail: "GPU quota, provided dataset and prohibited external downloads",
        status: "Scheduled",
        owner: "Labs Operations",
        destination: "sessions",
      },
      {
        name: "Graphics & CAD Practical",
        detail: "2D/3D editor, approved asset pack and export evidence",
        status: "Preflight",
        owner: "Labs Operations",
        destination: "exam-room",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-labs-intended-lab-environments-lab-environment-catalog",
    model: "environment",
    layoutGroup: "cards",
  },
  "active-practical-exams": {
    id: "active-practical-exams",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Active Practical Exams",
    description: "Aggregate session state, runtime safety signals and authorized live handoff to Labs.",
    group: "Live Operations",
    icon: "monitor",
    family: "practical-control-room",
    source: "candidates",
    primary: { label: "Open Labs Exam Room", intent: "lab:exam-room", icon: "flask" },
    secondary: [
      { label: "Runtime safety signals", intent: "navigate:labs:runtime-safety-signals", icon: "pulse" },
      { label: "Returned evidence", intent: "navigate:labs:returned-evidence", icon: "database" },
    ],
    metrics: [
      {
        label: "Active candidates",
        value: "24",
        trend: "Agentic Workflow practical",
        icon: "users",
        tone: "info",
      },
      { label: "Runtime healthy", value: "21", trend: "3 support cases", icon: "pulse", tone: "good" },
      { label: "Human attention", value: "3", trend: "No automatic outcome", icon: "eye", tone: "warn" },
      { label: "Compute quota", value: "63", trend: "Aggregate utilization", icon: "flask", tone: "violet" },
    ],
    tabs: ["Candidate runtimes", "Support queue", "Runtime timeline"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "candidateNo", label: "Number" },
      { key: "lastEvent", label: "Latest runtime event" },
      { key: "network", label: "Connection" },
      { key: "timeRemaining", label: "Remaining" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-labs-active-practical-exams-practical-control-room",
    model: "practical",
    layoutGroup: "live",
  },
  "returned-evidence": {
    id: "returned-evidence",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Returned Evidence",
    description:
      "Consented and policy-approved evidence returned from Labs to the Academy assessment record.",
    group: "Evidence Sync",
    icon: "inbox",
    family: "returned-evidence",
    source: "evidence",
    primary: { label: "Inspect returned evidence", intent: "modal:evidence-detail", icon: "database" },
    secondary: [
      { label: "Evidence inventory", intent: "navigate:evidence:evidence-records", icon: "archive" },
      { label: "Chain of custody", intent: "navigate:evidence:chain-of-custody", icon: "link" },
    ],
    metrics: [
      {
        label: "Returned manifests",
        value: "18",
        trend: "Labs-native references",
        icon: "database",
        tone: "info",
      },
      { label: "Hash verified", value: "18", trend: "No mismatch", icon: "check", tone: "good" },
      { label: "Pending selection", value: "4", trend: "Assessor-controlled", icon: "eye", tone: "warn" },
      {
        label: "Restricted payloads",
        value: "7",
        trend: "Proctor cannot open",
        icon: "lock",
        tone: "violet",
      },
    ],
    tabs: ["Returned manifests", "Selected for review", "Access history"],
    columns: [
      { key: "id", label: "Evidence reference" },
      { key: "type", label: "Artifact type" },
      { key: "candidate", label: "Candidate" },
      { key: "source", label: "Labs source" },
      { key: "hash", label: "Integrity" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Labs remains authoritative",
        body: "The Academy stores the returned manifest and authorized reference; it does not copy private prompts or files by default.",
        icon: "flask",
      },
      {
        title: "Assessor selects academic evidence",
        body: "The proctor preserves availability and chain of custody but does not determine academic value.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-labs-returned-evidence-returned-evidence",
    model: "evidence",
    layoutGroup: "queue",
  },
  "runtime-safety-signals": {
    id: "runtime-safety-signals",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Runtime Safety Signals",
    description: "Compute, tool, network, environment and emergency-stop signals owned by Labs.",
    group: "Safety",
    icon: "shield",
    family: "runtime-signal-console",
    source: "static",
    primary: { label: "Open runtime signal", intent: "modal:secure-note", icon: "pulse" },
    secondary: [
      { label: "Technical incidents", intent: "navigate:incidents:technical-incidents", icon: "monitor" },
      { label: "Labs control room", intent: "navigate:labs:active-practical-exams", icon: "flask" },
    ],
    metrics: [
      { label: "Active signals", value: "5", trend: "Operational attention", icon: "pulse", tone: "warn" },
      { label: "Compute limit", value: "2", trend: "Approaching quota", icon: "flask", tone: "info" },
      { label: "Tool-denied events", value: "3", trend: "Policy worked", icon: "lock", tone: "good" },
      { label: "Transferred incidents", value: "1", trend: "Human review", icon: "alert", tone: "violet" },
    ],
    tabs: ["Live signals", "Policy denials", "Transferred"],
    columns: [
      { key: "name", label: "Runtime signal" },
      { key: "detail", label: "Observable event" },
      { key: "source", label: "Source" },
      { key: "owner", label: "Human / service owner" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Runtime latency above target",
        detail: "Candidate CND-2026042 · notebook execution 8.4 seconds",
        status: "Monitoring",
        owner: "Labs Technical Support",
        source: "Runtime telemetry",
      },
      {
        name: "External network request denied",
        detail: "Policy blocked an unapproved destination; candidate work preserved",
        status: "Resolved",
        owner: "Labs Policy Engine",
        source: "Tool gateway",
      },
      {
        name: "GPU quota at 82%",
        detail: "Deep Learning practical · 18 minutes remaining",
        status: "Needs attention",
        owner: "Labs Operations",
        source: "Compute service",
      },
      {
        name: "File import exceeded approved size",
        detail: "Candidate received neutral procedural guidance",
        status: "Resolved",
        owner: "Room Proctor",
        source: "Artifact service",
      },
      {
        name: "Evaluation harness unavailable",
        detail: "Two candidate runs paused under approved continuity procedure",
        status: "Escalated",
        owner: "Technical Lead",
        source: "Evaluation service",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-labs-runtime-safety-signals-runtime-signal-console",
    model: "queue",
    layoutGroup: "diagnostics",
  },
  "labs-data-boundaries": {
    id: "labs-data-boundaries",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Labs Data Boundaries",
    description:
      "What Academy proctors can see, what remains private in Labs and how scope is independently verified.",
    group: "Governance",
    icon: "lock",
    family: "labs-boundary-map",
    source: "static",
    primary: { label: "Review Labs boundary", intent: "modal:scope-boundary", icon: "lock" },
    secondary: [
      { label: "Privacy policy", intent: "navigate:governance:privacy-data-minimization", icon: "shield" },
      { label: "Returned evidence", intent: "navigate:labs:returned-evidence", icon: "database" },
    ],
    metrics: [
      { label: "Allowed claims", value: "11", trend: "One-time handoff", icon: "check", tone: "good" },
      {
        label: "Blocked fields",
        value: "23",
        trend: "Private learner content",
        icon: "lock",
        tone: "violet",
      },
      {
        label: "Return events",
        value: "8",
        trend: "Metadata only by default",
        icon: "database",
        tone: "info",
      },
      { label: "Replay window", value: "≤5 min", trend: "Redis-protected", icon: "clock", tone: "warn" },
    ],
    tabs: ["Handoff claims", "Runtime access", "Evidence return", "Audit"],
    columns: [
      { key: "name", label: "Data flow" },
      { key: "detail", label: "Boundary" },
      { key: "boundary", label: "Control" },
      { key: "owner", label: "Authority" },
      { key: "status", label: "Access" },
    ],
    rows: [
      {
        name: "Academy → Labs handoff",
        detail: "Proctor ID, assignment, session, destination, candidate-scope hash and language",
        status: "Allowed",
        owner: "Academy IAM",
        boundary: "One-time token",
      },
      {
        name: "Candidate prompts and source files",
        detail: "Not visible to the proctor unless the practical protocol grants a precise purpose",
        status: "Protected",
        owner: "Labs Runtime",
        boundary: "Field-level access",
      },
      {
        name: "Runtime safety events",
        detail: "Operational metadata may return for continuity and human attention",
        status: "Allowed",
        owner: "Labs Operations",
        boundary: "Event contract",
      },
      {
        name: "Academic evaluation output",
        detail: "Returned to assessor workflow; not a proctor decision surface",
        status: "Restricted",
        owner: "Assessment Service",
        boundary: "Segregated role",
      },
      {
        name: "Evidence manifest",
        detail: "Content hash, type, source, timestamps and authorized reference",
        status: "Allowed",
        owner: "Evidence Service",
        boundary: "Purpose-scoped",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-labs-labs-data-boundaries-labs-boundary-map",
    model: "boundary",
    layoutGroup: "policy",
  },
  "incident-queue": {
    id: "incident-queue",
    area: "incidents",
    areaLabel: "Incidents & Human Review",
    title: "Incident Queue",
    description: "Signals and factual events waiting for review, escalation or closure by authorized humans.",
    group: "Incident Management",
    icon: "inbox",
    family: "incident-queue",
    source: "incidents",
    primary: { label: "Record incident", intent: "modal:record-incident", icon: "plus" },
    secondary: [
      { label: "Escalations", intent: "navigate:incidents:escalations", icon: "arrow" },
      { label: "Closed incidents", intent: "navigate:incidents:closed-incidents", icon: "archive" },
    ],
    metrics: [
      { label: "Open", value: "9", trend: "Factual records", icon: "alert", tone: "warn" },
      { label: "Needs review", value: "5", trend: "Authorized reviewer", icon: "eye", tone: "violet" },
      { label: "Technical", value: "2", trend: "Kept separate", icon: "pulse", tone: "info" },
      { label: "Resolved", value: "12", trend: "With due process", icon: "check", tone: "good" },
    ],
    tabs: ["Open queue", "Monitoring", "Transferred", "Closed"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-incidents-incident-queue-incident-queue",
    model: "special",
    layoutGroup: "queue",
  },
  "record-incident": {
    id: "record-incident",
    area: "incidents",
    areaLabel: "Incidents & Human Review",
    title: "Record Incident",
    description:
      "Record observable facts, source, time, action, evidence and handover without declaring misconduct.",
    group: "Incident Management",
    icon: "plus",
    family: "incident-form",
    source: "static",
    primary: { label: "Create factual incident", intent: "modal:record-incident", icon: "plus" },
    secondary: [
      { label: "Incident queue", intent: "navigate:incidents:incident-queue", icon: "alert" },
      { label: "Authority boundary", intent: "navigate:governance:authority-boundaries", icon: "shield" },
    ],
    metrics: [
      {
        label: "Required fields",
        value: "8",
        trend: "Facts, source and action",
        icon: "clipboard",
        tone: "info",
      },
      { label: "Outcome fields", value: "0", trend: "Not available to proctor", icon: "lock", tone: "good" },
      {
        label: "Evidence optional",
        value: "Minimum necessary",
        trend: "database",
        icon: "violet",
        tone: "info",
      },
      { label: "Human owner", value: "Required", trend: "Named handover", icon: "users", tone: "warn" },
    ],
    tabs: ["Observable facts", "Support action", "Evidence references", "Handover"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Do not label misconduct",
        body: "The record captures what was observed and what support action was taken. It does not decide an outcome.",
        icon: "shield",
      },
      {
        title: "Candidate context remains separate",
        body: "Statements, translations, technical logs and human observations are preserved as distinct records.",
        icon: "message",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: [
      "Assigned session",
      "Candidate or room scope",
      "Observable event",
      "Source and timestamp",
      "Neutral support action",
      "Evidence references",
      "Candidate statement request",
      "Next authorized owner",
    ],
    statusKey: "status",
    signature: "EP2-incidents-record-incident-incident-form",
    model: "form",
    layoutGroup: "form",
  },
  "evidence-review": {
    id: "evidence-review",
    area: "incidents",
    areaLabel: "Incidents & Human Review",
    title: "Evidence Review",
    description: "Review authorized event sequences and linked evidence before escalation or closure.",
    group: "Human Review",
    icon: "eye",
    family: "incident-evidence-review",
    source: "evidence",
    primary: { label: "Open evidence record", intent: "modal:evidence-detail", icon: "eye" },
    secondary: [
      { label: "Incident queue", intent: "navigate:incidents:incident-queue", icon: "alert" },
      { label: "Custody ledger", intent: "navigate:evidence:chain-of-custody", icon: "link" },
    ],
    metrics: [
      { label: "Linked records", value: "8", trend: "Across 5 incidents", icon: "database", tone: "info" },
      { label: "Hash verified", value: "8", trend: "No mismatch", icon: "check", tone: "good" },
      { label: "Minimum scope", value: "6", trend: "Reviewer access", icon: "lock", tone: "violet" },
      { label: "Missing context", value: "2", trend: "Statement requested", icon: "message", tone: "warn" },
    ],
    tabs: ["Linked evidence", "Missing context", "Access history"],
    columns: [
      { key: "id", label: "Evidence ID" },
      { key: "type", label: "Type" },
      { key: "candidate", label: "Candidate" },
      { key: "source", label: "Source" },
      { key: "hash", label: "Integrity" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-incidents-evidence-review-incident-evidence-review",
    model: "evidence",
    layoutGroup: "queue",
  },
  "candidate-statements": {
    id: "candidate-statements",
    area: "incidents",
    areaLabel: "Incidents & Human Review",
    title: "Candidate Statements",
    description: "Invite and retain the candidate’s explanation in their own words and language.",
    group: "Human Review",
    icon: "message",
    family: "statement-inbox",
    source: "static",
    primary: { label: "Request candidate statement", intent: "modal:candidate-message", icon: "message" },
    secondary: [
      { label: "Incident queue", intent: "navigate:incidents:incident-queue", icon: "alert" },
      { label: "Language settings", intent: "navigate:governance:language-accessibility", icon: "globe" },
    ],
    metrics: [
      { label: "Requested", value: "4", trend: "Candidate chooses response", icon: "message", tone: "info" },
      { label: "Received", value: "3", trend: "Original language retained", icon: "check", tone: "good" },
      {
        label: "Translation pending",
        value: "1",
        trend: "Human-approved service",
        icon: "globe",
        tone: "warn",
      },
      { label: "Protected", value: "4", trend: "Immutable after submission", icon: "lock", tone: "violet" },
    ],
    tabs: ["Requests", "Received", "Translations", "Access"],
    columns: [
      { key: "name", label: "Candidate" },
      { key: "detail", label: "Statement context" },
      { key: "time", label: "Requested / received" },
      { key: "owner", label: "Authorized owner" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Linda Nsubuga",
        detail: "Statement requested for INC-2026-0041 · English",
        status: "Received",
        owner: "Integrity Review",
        time: "15:08 EAT",
      },
      {
        name: "Mariam Nabwire",
        detail: "Statement requested for INC-2026-0044 · French original",
        status: "Translation pending",
        owner: "Language Services",
        time: "15:11 EAT",
      },
      {
        name: "Brian Okello",
        detail: "Accessibility incident context · candidate chose Luganda",
        status: "Received",
        owner: "Accessibility Review",
        time: "14:57 EAT",
      },
      {
        name: "Ruth Nakato",
        detail: "Room-environment clarification requested",
        status: "Awaiting candidate",
        owner: "Lead Proctor",
        time: "14:52 EAT",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-incidents-candidate-statements-statement-inbox",
    model: "statements",
    layoutGroup: "queue",
  },
  escalations: {
    id: "escalations",
    area: "incidents",
    areaLabel: "Incidents & Human Review",
    title: "Escalations",
    description:
      "Route integrity, safeguarding, technical, accessibility and wellbeing matters to the correct authority.",
    group: "Human Review",
    icon: "arrow",
    family: "escalation-board",
    source: "incidents",
    primary: { label: "Create escalation", intent: "modal:secure-note", icon: "arrow" },
    secondary: [
      { label: "Emergency contacts", intent: "navigate:communications:emergency-contacts", icon: "users" },
      { label: "Handover notes", intent: "navigate:sessions:handover-notes", icon: "history" },
    ],
    metrics: [
      { label: "Technical", value: "3", trend: "Support owner", icon: "monitor", tone: "info" },
      {
        label: "Accessibility",
        value: "2",
        trend: "Protected pathway",
        icon: "accessibility",
        tone: "violet",
      },
      { label: "Integrity review", value: "5", trend: "Human reviewer", icon: "shield", tone: "warn" },
      { label: "Safeguarding", value: "1", trend: "Restricted", icon: "lock", tone: "danger" },
    ],
    tabs: ["Awaiting transfer", "Accepted", "Needs context", "Closed"],
    columns: [
      { key: "id", label: "Incident" },
      { key: "category", label: "Pathway" },
      { key: "summary", label: "Factual context" },
      { key: "owner", label: "Current owner" },
      { key: "status", label: "Transfer state" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-incidents-escalations-escalation-board",
    model: "board",
    layoutGroup: "queue",
  },
  "technical-incidents": {
    id: "technical-incidents",
    area: "incidents",
    areaLabel: "Incidents & Human Review",
    title: "Technical Incidents",
    description:
      "Connectivity, power, browser, media, runtime and platform incidents separated from integrity findings.",
    group: "Incident Types",
    icon: "monitor",
    family: "technical-incident-queue",
    source: "incidents",
    primary: { label: "Open technical case", intent: "modal:incident-detail", icon: "monitor" },
    secondary: [
      { label: "Technical support", intent: "navigate:communications:technical-support", icon: "help" },
      { label: "Device logs", intent: "navigate:evidence:device-network-logs", icon: "database" },
    ],
    metrics: [
      {
        label: "Open cases",
        value: "4",
        trend: "Connection, device, platform",
        icon: "monitor",
        tone: "warn",
      },
      { label: "Resolved in session", value: "11", trend: "Work preserved", icon: "check", tone: "good" },
      { label: "Median response", value: "2.8", trend: "Minutes", icon: "clock", tone: "info" },
      {
        label: "Transferred to integrity",
        value: "0",
        trend: "Separate by default",
        icon: "shield",
        tone: "violet",
      },
    ],
    tabs: ["Open technical", "Monitoring", "Resolved"],
    columns: [
      { key: "id", label: "Case" },
      { key: "candidate", label: "Candidate" },
      { key: "summary", label: "Technical event" },
      { key: "action", label: "Support action" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-incidents-technical-incidents-technical-incident-queue",
    model: "queue",
    layoutGroup: "queue",
  },
  "closed-incidents": {
    id: "closed-incidents",
    area: "incidents",
    areaLabel: "Incidents & Human Review",
    title: "Closed Incidents",
    description: "Resolved, dismissed, transferred and completed records with immutable history.",
    group: "Records",
    icon: "archive",
    family: "closed-incident-ledger",
    source: "incidents",
    primary: { label: "Open closed record", intent: "modal:incident-detail", icon: "archive" },
    secondary: [
      { label: "Incident analytics", intent: "navigate:reports:incident-analytics", icon: "chart" },
      { label: "Export authorized records", intent: "export:closed-incidents", icon: "download" },
    ],
    metrics: [
      {
        label: "Closed this month",
        value: "27",
        trend: "Human outcome elsewhere",
        icon: "archive",
        tone: "good",
      },
      { label: "Technical only", value: "14", trend: "No integrity transfer", icon: "pulse", tone: "info" },
      { label: "With statement", value: "8", trend: "Original retained", icon: "message", tone: "violet" },
      { label: "Retention due", value: "3", trend: "Next 30 days", icon: "clock", tone: "warn" },
    ],
    tabs: ["Recently closed", "Technical resolution", "Transferred outcome"],
    columns: [
      { key: "id", label: "Record" },
      { key: "candidate", label: "Candidate" },
      { key: "category", label: "Category" },
      { key: "facts", label: "Recorded facts" },
      { key: "owner", label: "Final operational owner" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-incidents-closed-incidents-closed-incident-ledger",
    model: "register",
    layoutGroup: "table",
  },
  "evidence-records": {
    id: "evidence-records",
    area: "evidence",
    areaLabel: "Evidence & Chain of Custody",
    title: "Evidence Records",
    description:
      "Session-scoped evidence inventory with source, integrity hash, custodian, access and retention.",
    group: "Evidence",
    icon: "database",
    family: "evidence-inventory",
    source: "evidence",
    primary: { label: "Open evidence record", intent: "modal:evidence-detail", icon: "eye" },
    secondary: [
      { label: "Prepare export", intent: "navigate:evidence:export-packages", icon: "download" },
      { label: "Custody ledger", intent: "navigate:evidence:chain-of-custody", icon: "link" },
    ],
    metrics: [
      { label: "Evidence records", value: "8", trend: "Assigned sessions", icon: "database", tone: "info" },
      { label: "Hashes verified", value: "8", trend: "Content-addressed", icon: "check", tone: "good" },
      { label: "Custodians", value: "4", trend: "Least privilege", icon: "users", tone: "violet" },
      { label: "Access exceptions", value: "0", trend: "No unauthorized view", icon: "lock", tone: "good" },
    ],
    tabs: ["Inventory", "Access history", "Integrity checks"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-evidence-evidence-records-evidence-inventory",
    model: "special",
    layoutGroup: "evidence",
  },
  "session-timelines": {
    id: "session-timelines",
    area: "evidence",
    areaLabel: "Evidence & Chain of Custody",
    title: "Session Timelines",
    description: "Chronological candidate, room, system, communication and human-action events.",
    group: "Evidence",
    icon: "history",
    family: "session-event-timeline",
    source: "static",
    primary: { label: "Open session timeline", intent: "modal:session-detail", icon: "history" },
    secondary: [
      { label: "Evidence records", intent: "navigate:evidence:evidence-records", icon: "database" },
      { label: "Export timeline", intent: "export:timeline", icon: "download" },
    ],
    metrics: [
      { label: "Timeline events", value: "142", trend: "Current room", icon: "history", tone: "info" },
      { label: "Candidate events", value: "116", trend: "Session-scoped", icon: "users", tone: "good" },
      { label: "Control events", value: "9", trend: "Reason + authority", icon: "sliders", tone: "violet" },
      { label: "Evidence seals", value: "18", trend: "No hash failures", icon: "lock", tone: "warn" },
    ],
    tabs: ["Session timeline", "Candidate sequence", "Control history"],
    columns: [
      { key: "name", label: "Event" },
      { key: "detail", label: "Factual record" },
      { key: "type", label: "Event type" },
      { key: "owner", label: "Actor / service" },
      { key: "status", label: "Integrity" },
    ],
    rows: [
      {
        name: "14:00:00 · Session started",
        detail: "Version 1 · 38 candidate assignments activated",
        status: "Sealed",
        owner: "Academy Session Service",
        type: "Control",
      },
      {
        name: "14:03:22 · Candidate entry complete",
        detail: "36 present · 2 approved late entries",
        status: "Recorded",
        owner: "Lead Proctor",
        type: "Presence",
      },
      {
        name: "14:29:02 · Room audio event",
        detail: "Human observation; no conclusion recorded",
        status: "Linked",
        owner: "Grace Namusoke",
        type: "Observation",
      },
      {
        name: "14:41:06 · Candidate disconnected",
        detail: "Timer and work preserved · technical support opened",
        status: "Resolved",
        owner: "Peter Ouma",
        type: "Technical",
      },
      {
        name: "14:44:18 · Focus events reviewed",
        detail: "Four events linked to incident INC-2026-0041",
        status: "Transferred",
        owner: "Amina Mutesi",
        type: "Evidence",
      },
      {
        name: "15:00:00 · Relief handover due",
        detail: "Lead coverage transfers to Joel Ssempala",
        status: "Upcoming",
        owner: "Amina Mutesi",
        type: "Handover",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-evidence-session-timelines-session-event-timeline",
    model: "timeline",
    layoutGroup: "timeline",
  },
  "screenshots-clips": {
    id: "screenshots-clips",
    area: "evidence",
    areaLabel: "Evidence & Chain of Custody",
    title: "Screenshots & Clips",
    description: "Authorized captures and clips with purpose, provenance, redaction and access control.",
    group: "Evidence",
    icon: "monitor",
    family: "media-evidence-library",
    source: "evidence",
    primary: { label: "Open authorized media", intent: "modal:evidence-detail", icon: "eye" },
    secondary: [
      { label: "Retention & access", intent: "navigate:evidence:retention-access", icon: "lock" },
      { label: "Export package", intent: "navigate:evidence:export-packages", icon: "download" },
    ],
    metrics: [
      { label: "Screenshots", value: "5", trend: "Event-scoped", icon: "camera", tone: "info" },
      { label: "Clips", value: "3", trend: "Published policy", icon: "video", tone: "violet" },
      { label: "Redacted previews", value: "8", trend: "Minimum necessary", icon: "eye", tone: "good" },
      { label: "Access requests", value: "2", trend: "Approval pending", icon: "lock", tone: "warn" },
    ],
    tabs: ["Screenshots", "Clips", "Redacted previews", "Access log"],
    columns: [
      { key: "id", label: "Media reference" },
      { key: "type", label: "Media type" },
      { key: "candidate", label: "Candidate" },
      { key: "captured", label: "Captured" },
      { key: "access", label: "Access scope" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-evidence-screenshots-clips-media-evidence-library",
    model: "media",
    layoutGroup: "media",
  },
  "device-network-logs": {
    id: "device-network-logs",
    area: "evidence",
    areaLabel: "Evidence & Chain of Custody",
    title: "Device & Network Logs",
    description: "Approved browser, connectivity and device telemetry retained under policy.",
    group: "Evidence",
    icon: "pulse",
    family: "device-log-explorer",
    source: "evidence",
    primary: { label: "Inspect technical log", intent: "modal:evidence-detail", icon: "pulse" },
    secondary: [
      { label: "Technical incidents", intent: "navigate:incidents:technical-incidents", icon: "monitor" },
      { label: "Reliability report", intent: "navigate:reports:technical-reliability", icon: "chart" },
    ],
    metrics: [
      { label: "Log records", value: "16", trend: "Current assignments", icon: "database", tone: "info" },
      { label: "Connection events", value: "9", trend: "Technical scope", icon: "pulse", tone: "warn" },
      { label: "Device checks", value: "7", trend: "Published preflight", icon: "monitor", tone: "good" },
      { label: "Integrity hashes", value: "16", trend: "Verified", icon: "lock", tone: "violet" },
    ],
    tabs: ["Network logs", "Device checks", "Service events"],
    columns: [
      { key: "id", label: "Log reference" },
      { key: "type", label: "Log type" },
      { key: "candidate", label: "Candidate" },
      { key: "source", label: "Source service" },
      { key: "captured", label: "Window" },
      { key: "status", label: "Integrity" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-evidence-device-network-logs-device-log-explorer",
    model: "logs",
    layoutGroup: "diagnostics",
  },
  "chain-of-custody": {
    id: "chain-of-custody",
    area: "evidence",
    areaLabel: "Evidence & Chain of Custody",
    title: "Chain of Custody",
    description: "Capture, hash, transfer, access, export and disposition history.",
    group: "Integrity",
    icon: "link",
    family: "custody-ledger",
    source: "evidence",
    primary: { label: "Record custody transfer", intent: "modal:secure-note", icon: "link" },
    secondary: [
      { label: "Evidence inventory", intent: "navigate:evidence:evidence-records", icon: "database" },
      { label: "Access rules", intent: "navigate:evidence:retention-access", icon: "lock" },
    ],
    metrics: [
      { label: "Transfers today", value: "12", trend: "All acknowledged", icon: "link", tone: "info" },
      { label: "Custody exceptions", value: "0", trend: "No broken chain", icon: "check", tone: "good" },
      { label: "Sealed records", value: "8", trend: "Hash verified", icon: "lock", tone: "violet" },
      { label: "Pending receipt", value: "1", trend: "Integrity Review", icon: "clock", tone: "warn" },
    ],
    tabs: ["Custody chain", "Transfers", "Integrity verification"],
    columns: [
      { key: "id", label: "Evidence" },
      { key: "type", label: "Record type" },
      { key: "custodian", label: "Current custodian" },
      { key: "hash", label: "Integrity hash" },
      { key: "access", label: "Access scope" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-evidence-chain-of-custody-custody-ledger",
    model: "custody",
    layoutGroup: "evidence",
  },
  "retention-access": {
    id: "retention-access",
    area: "evidence",
    areaLabel: "Evidence & Chain of Custody",
    title: "Retention & Access",
    description: "Purpose limitation, legal hold, minimum retention, deletion and role-scoped access.",
    group: "Governance",
    icon: "lock",
    family: "retention-access-register",
    source: "static",
    primary: { label: "Request authorized access", intent: "modal:secure-note", icon: "lock" },
    secondary: [
      { label: "Evidence inventory", intent: "navigate:evidence:evidence-records", icon: "database" },
      { label: "Privacy policy", intent: "navigate:governance:privacy-data-minimization", icon: "shield" },
    ],
    metrics: [
      { label: "Retention profiles", value: "6", trend: "Policy-versioned", icon: "clock", tone: "info" },
      { label: "Legal holds", value: "1", trend: "Protected from deletion", icon: "lock", tone: "violet" },
      { label: "Access grants", value: "7", trend: "Purpose + expiry", icon: "users", tone: "good" },
      { label: "Expiring grants", value: "2", trend: "Within 24 hours", icon: "alert", tone: "warn" },
    ],
    tabs: ["Retention rules", "Access grants", "Legal holds", "Deletion queue"],
    columns: [
      { key: "name", label: "Record class" },
      { key: "detail", label: "Purpose" },
      { key: "status", label: "Retention" },
      { key: "access", label: "Authorized access" },
      { key: "owner", label: "Policy owner" },
    ],
    rows: [
      {
        name: "Technical network logs",
        detail: "Operational troubleshooting and reliability review",
        status: "90 days",
        owner: "Technical Operations",
        access: "Technical + assigned proctor",
      },
      {
        name: "Incident evidence",
        detail: "Human integrity review and appeal support",
        status: "180 days",
        owner: "Integrity Review",
        access: "Purpose-limited reviewers",
      },
      {
        name: "Oral exam recording",
        detail: "Assessment record; caption and language profile retained",
        status: "180 days",
        owner: "Assessment Operations",
        access: "Panel + authorized appeal",
      },
      {
        name: "Accessibility support event",
        detail: "Protected operational record; no diagnosis displayed",
        status: "Case policy",
        owner: "Accessibility Office",
        access: "Protected support roles",
      },
      {
        name: "Safeguarding matter",
        detail: "Legal hold applied; normal deletion suspended",
        status: "Legal hold",
        owner: "Safeguarding Lead",
        access: "Restricted",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-evidence-retention-access-retention-access-register",
    model: "policy",
    layoutGroup: "evidence",
  },
  "export-packages": {
    id: "export-packages",
    area: "evidence",
    areaLabel: "Evidence & Chain of Custody",
    title: "Export Packages",
    description: "Manifested, permission-checked evidence packages for authorized reviewers.",
    group: "Evidence",
    icon: "download",
    family: "export-package-builder",
    source: "evidence",
    primary: { label: "Prepare governed export", intent: "modal:evidence-export", icon: "download" },
    secondary: [
      { label: "Access rules", intent: "navigate:evidence:retention-access", icon: "lock" },
      { label: "Custody ledger", intent: "navigate:evidence:chain-of-custody", icon: "link" },
    ],
    metrics: [
      { label: "Draft exports", value: "2", trend: "Purpose not yet approved", icon: "file", tone: "warn" },
      { label: "Ready packages", value: "3", trend: "Encrypted + expiring", icon: "check", tone: "good" },
      { label: "Downloads today", value: "4", trend: "All audited", icon: "download", tone: "info" },
      { label: "Revoked links", value: "1", trend: "Recipient changed", icon: "lock", tone: "violet" },
    ],
    tabs: ["Package builder", "Approval queue", "Download history"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: [
      "Declared purpose",
      "Authorized recipient",
      "Included evidence references",
      "Field-level scope",
      "Retention / expiry",
      "Encryption profile",
      "Approval reference",
      "Download limit",
    ],
    statusKey: "status",
    signature: "EP2-evidence-export-packages-export-package-builder",
    model: "builder",
    layoutGroup: "evidence",
  },
  "approved-accommodations": {
    id: "approved-accommodations",
    area: "accommodations",
    areaLabel: "Accommodations & Accessibility",
    title: "Approved Accommodations",
    description: "Session-scoped accommodations supplied by authorized accessibility staff.",
    group: "Accommodations",
    icon: "accessibility",
    family: "approved-accommodations",
    source: "accommodations",
    primary: { label: "Open approved plan", intent: "modal:accommodation-detail", icon: "accessibility" },
    secondary: [
      { label: "Acknowledge plans", intent: "action:advance", icon: "check" },
      {
        label: "Accessibility support",
        intent: "navigate:communications:candidate-help-queue",
        icon: "help",
      },
    ],
    metrics: [
      {
        label: "Active plans",
        value: "8",
        trend: "Session-relevant only",
        icon: "accessibility",
        tone: "info",
      },
      { label: "Acknowledged", value: "7", trend: "One due", icon: "check", tone: "good" },
      { label: "In use", value: "2", trend: "Timers protected", icon: "clock", tone: "violet" },
      { label: "Barriers", value: "1", trend: "Support active", icon: "alert", tone: "warn" },
    ],
    tabs: ["Approved plans", "Acknowledgement", "Delivery status"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-accommodations-approved-accommodations-approved-accommodations",
    model: "special",
    layoutGroup: "table",
  },
  "extra-time-breaks": {
    id: "extra-time-breaks",
    area: "accommodations",
    areaLabel: "Accommodations & Accessibility",
    title: "Extra Time & Breaks",
    description: "Pre-approved time, pause, break, rest and re-entry arrangements.",
    group: "Accommodations",
    icon: "clock",
    family: "time-break-manager",
    source: "accommodations",
    primary: { label: "Apply approved timer action", intent: "modal:break-action", icon: "clock" },
    secondary: [
      { label: "Breaks & re-entry", intent: "navigate:rooms:breaks-reentry", icon: "history" },
      { label: "Delivery report", intent: "navigate:reports:accommodation-delivery", icon: "chart" },
    ],
    metrics: [
      { label: "Extra-time plans", value: "5", trend: "Server-calculated", icon: "clock", tone: "info" },
      {
        label: "Medical breaks",
        value: "2",
        trend: "Approved instructions",
        icon: "accessibility",
        tone: "violet",
      },
      { label: "Timer pauses", value: "3", trend: "Reason recorded", icon: "history", tone: "warn" },
      { label: "Unauthorized changes", value: "0", trend: "Controls enforced", icon: "lock", tone: "good" },
    ],
    tabs: ["Extra time", "Approved breaks", "Timer history"],
    columns: [
      { key: "candidate", label: "Candidate" },
      { key: "type", label: "Approved adjustment" },
      { key: "session", label: "Session" },
      { key: "instructions", label: "Proctor instruction" },
      { key: "status", label: "Delivery" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-accommodations-extra-time-breaks-time-break-manager",
    model: "timers",
    layoutGroup: "controls",
  },
  "assistive-technology": {
    id: "assistive-technology",
    area: "accommodations",
    areaLabel: "Accommodations & Accessibility",
    title: "Assistive Technology",
    description: "Screen readers, magnification, alternative input, approved aids and compatibility checks.",
    group: "Accommodations",
    icon: "monitor",
    family: "assistive-tech-readiness",
    source: "accommodations",
    primary: {
      label: "Record assistive technology check",
      intent: "action:test-device",
      icon: "accessibility",
    },
    secondary: [
      { label: "Device checks", intent: "navigate:readiness:device-network-checks", icon: "monitor" },
      {
        label: "Accessibility incident",
        intent: "navigate:accommodations:accessibility-incidents",
        icon: "alert",
      },
    ],
    metrics: [
      {
        label: "Screen readers",
        value: "4",
        trend: "Compatibility checked",
        icon: "accessibility",
        tone: "good",
      },
      { label: "Keyboard-only", value: "3", trend: "Alternative panel ready", icon: "monitor", tone: "info" },
      { label: "Magnification", value: "2", trend: "Secure zoom profile", icon: "eye", tone: "violet" },
      { label: "Open barrier", value: "1", trend: "Focus recovery", icon: "alert", tone: "warn" },
    ],
    tabs: ["Approved tools", "Compatibility checks", "Fallback path"],
    columns: [
      { key: "candidate", label: "Candidate" },
      { key: "type", label: "Assistive technology" },
      { key: "instructions", label: "Approved setup" },
      { key: "owner", label: "Issuing authority" },
      { key: "status", label: "Readiness" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-accommodations-assistive-technology-assistive-tech-readiness",
    model: "checklist",
    layoutGroup: "checklist",
  },
  "captions-interpretation": {
    id: "captions-interpretation",
    area: "accommodations",
    areaLabel: "Accommodations & Accessibility",
    title: "Captions & Interpretation",
    description: "Captions, sign-language interpretation, language support and communication preferences.",
    group: "Accommodations",
    icon: "message",
    family: "language-service-desk",
    source: "accommodations",
    primary: { label: "Confirm caption / interpreter readiness", intent: "action:advance", icon: "message" },
    secondary: [
      { label: "Class recordings", intent: "navigate:class:recordings-captions", icon: "play" },
      { label: "Language settings", intent: "navigate:governance:language-accessibility", icon: "globe" },
    ],
    metrics: [
      { label: "Caption plans", value: "4", trend: "Class sessions", icon: "message", tone: "good" },
      { label: "Interpreters", value: "2", trend: "Confirmed", icon: "users", tone: "info" },
      { label: "Languages", value: "5", trend: "Original retained", icon: "globe", tone: "violet" },
      { label: "Pending test", value: "1", trend: "Before panel entry", icon: "clock", tone: "warn" },
    ],
    tabs: ["Captions", "Interpretation", "Transcript scope", "Readiness"],
    columns: [
      { key: "candidate", label: "Candidate" },
      { key: "type", label: "Language service" },
      { key: "session", label: "Session" },
      { key: "instructions", label: "Delivery instruction" },
      { key: "status", label: "Readiness" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-accommodations-captions-interpretation-language-service-desk",
    model: "directory",
    layoutGroup: "checklist",
  },
  "alternative-verification": {
    id: "alternative-verification",
    area: "accommodations",
    areaLabel: "Accommodations & Accessibility",
    title: "Alternative Verification",
    description: "Approved non-standard identity and environment verification procedures.",
    group: "Accommodations",
    icon: "user",
    family: "alternative-verification-paths",
    source: "accommodations",
    primary: { label: "Open approved alternative", intent: "modal:accommodation-detail", icon: "user" },
    secondary: [
      { label: "Identity verification", intent: "navigate:readiness:identity-verification", icon: "user" },
      { label: "Record barrier", intent: "navigate:accommodations:accessibility-incidents", icon: "alert" },
    ],
    metrics: [
      {
        label: "Approved alternatives",
        value: "3",
        trend: "No autonomous biometric match",
        icon: "user",
        tone: "good",
      },
      { label: "Completed today", value: "2", trend: "Human verified", icon: "check", tone: "info" },
      { label: "Awaiting support", value: "1", trend: "Accessibility office", icon: "clock", tone: "warn" },
      {
        label: "Unauthorized alternatives",
        value: "0",
        trend: "Controls enforced",
        icon: "lock",
        tone: "violet",
      },
    ],
    tabs: ["Approved paths", "Current queue", "Verification audit"],
    columns: [
      { key: "candidate", label: "Candidate" },
      { key: "type", label: "Alternative method" },
      { key: "instructions", label: "Approved procedure" },
      { key: "owner", label: "Authority" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-accommodations-alternative-verification-alternative-verification-paths",
    model: "verification",
    layoutGroup: "checklist",
  },
  "accessibility-incidents": {
    id: "accessibility-incidents",
    area: "accommodations",
    areaLabel: "Accommodations & Accessibility",
    title: "Accessibility Incidents",
    description: "Failures or barriers requiring immediate support and independent follow-up.",
    group: "Accommodations",
    icon: "alert",
    family: "accessibility-incident-queue",
    source: "static",
    primary: { label: "Record accessibility barrier", intent: "modal:record-incident", icon: "alert" },
    secondary: [
      {
        label: "Approved plans",
        intent: "navigate:accommodations:approved-accommodations",
        icon: "accessibility",
      },
      { label: "Candidate support", intent: "navigate:communications:candidate-help-queue", icon: "help" },
    ],
    metrics: [
      { label: "Open barriers", value: "3", trend: "Support, not blame", icon: "alert", tone: "warn" },
      { label: "Resolved in session", value: "8", trend: "Access restored", icon: "check", tone: "good" },
      { label: "Timer protection", value: "4", trend: "Approved pauses", icon: "clock", tone: "info" },
      {
        label: "Protected handovers",
        value: "2",
        trend: "Accessibility office",
        icon: "lock",
        tone: "violet",
      },
    ],
    tabs: ["Active barriers", "Support actions", "Resolved"],
    columns: [
      { key: "name", label: "Barrier" },
      { key: "detail", label: "Candidate and support context" },
      { key: "time", label: "Time" },
      { key: "owner", label: "Protected owner" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Keyboard focus trapped in secure panel",
        detail: "David Ochieng · timer paused under approved plan",
        status: "Support active",
        owner: "Accessibility Support",
        time: "14:42 EAT",
      },
      {
        name: "Caption stream delayed",
        detail: "Mariam Nabwire · Class caption provider switched",
        status: "Resolved",
        owner: "Class Operations",
        time: "09:18 EAT",
      },
      {
        name: "Magnification reset after reconnect",
        detail: "Candidate restored to approved zoom profile",
        status: "Resolved",
        owner: "Technical Support",
        time: "13:54 EAT",
      },
      {
        name: "Alternative identity panel unavailable",
        detail: "Candidate waiting; standard check not forced",
        status: "Escalated",
        owner: "Accessibility Office",
        time: "08:47 EAT",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-accommodations-accessibility-incidents-accessibility-incident-queue",
    model: "queue",
    layoutGroup: "queue",
  },
  "secure-communications": {
    id: "secure-communications",
    area: "communications",
    areaLabel: "Communications & Support",
    title: "Secure Communications",
    description: "Session-scoped messages with candidates, proctor teams and authorized staff.",
    group: "Communication",
    icon: "message",
    family: "secure-communications",
    source: "static",
    primary: { label: "Send secure message", intent: "modal:candidate-message", icon: "send" },
    secondary: [
      { label: "Export current view", intent: "export:view", icon: "download" },
      { label: "Open role boundary", intent: "modal:scope-boundary", icon: "shield" },
    ],
    metrics: [
      { label: "Threads", value: "12", trend: "Assessment-scoped", icon: "message", tone: "info" },
      { label: "Unread", value: "7", trend: "Candidate help", icon: "inbox", tone: "warn" },
      { label: "Support channels", value: "4", trend: "Named authority", icon: "users", tone: "violet" },
      { label: "Resolved today", value: "31", trend: "Audited", icon: "check", tone: "good" },
    ],
    tabs: ["Candidate help", "Proctor team", "Specialist support"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-communications-secure-communications-secure-communications",
    model: "special",
    layoutGroup: "messages",
  },
  "candidate-help-queue": {
    id: "candidate-help-queue",
    area: "communications",
    areaLabel: "Communications & Support",
    title: "Candidate Help Queue",
    description: "Technical, procedural, accessibility and wellbeing requests during assessments.",
    group: "Support",
    icon: "help",
    family: "candidate-support-queue",
    source: "static",
    primary: { label: "Open help request", intent: "modal:candidate-message", icon: "help" },
    secondary: [
      { label: "Secure messages", intent: "navigate:rooms:secure-messages", icon: "message" },
      {
        label: "Accessibility support",
        intent: "navigate:accommodations:accessibility-incidents",
        icon: "accessibility",
      },
    ],
    metrics: [
      { label: "Waiting", value: "7", trend: "Oldest 3 minutes", icon: "help", tone: "warn" },
      { label: "Technical", value: "3", trend: "Support assigned", icon: "monitor", tone: "info" },
      { label: "Procedural", value: "2", trend: "Neutral guidance", icon: "book", tone: "good" },
      { label: "Accessibility", value: "2", trend: "Protected route", icon: "accessibility", tone: "violet" },
    ],
    tabs: ["Waiting", "Assigned", "Resolved"],
    columns: [
      { key: "name", label: "Help request" },
      { key: "detail", label: "Candidate message" },
      { key: "priority", label: "Waiting" },
      { key: "owner", label: "Assigned owner" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "CND-2026008 · Connection help",
        detail: "Candidate reconnected; needs confirmation of saved work",
        status: "Assigned",
        owner: "Peter Ouma",
        priority: "2 min",
      },
      {
        name: "CND-2026011 · Keyboard focus",
        detail: "Approved keyboard-only panel did not restore focus",
        status: "Urgent",
        owner: "Accessibility Support",
        priority: "3 min",
      },
      {
        name: "CND-2026014 · Allowed material question",
        detail: "Asks whether Academy reference sheet v3 is allowed",
        status: "Waiting",
        owner: "Room Proctor",
        priority: "4 min",
      },
      {
        name: "CND-2026022 · Break request",
        detail: "Named medical break plan available",
        status: "Waiting",
        owner: "Lead Proctor",
        priority: "4 min",
      },
      {
        name: "CND-2026031 · Class audio",
        detail: "Cannot hear panel chair; captions remain available",
        status: "Assigned",
        owner: "Class Support",
        priority: "5 min",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-communications-candidate-help-queue-candidate-support-queue",
    model: "support",
    layoutGroup: "queue",
  },
  "instructor-assessor-liaison": {
    id: "instructor-assessor-liaison",
    area: "communications",
    areaLabel: "Communications & Support",
    title: "Instructor & Assessor Liaison",
    description:
      "Clarifications and escalations without exposing answers or inviting grading by the proctor.",
    group: "Support",
    icon: "teacher",
    family: "assessor-liaison-board",
    source: "static",
    primary: { label: "Send assessor liaison message", intent: "modal:secure-note", icon: "message" },
    secondary: [
      { label: "Panel sessions", intent: "navigate:class:panel-huddle-sessions", icon: "users" },
      { label: "Escalations", intent: "navigate:incidents:escalations", icon: "arrow" },
    ],
    metrics: [
      { label: "Active liaisons", value: "5", trend: "Assessment-scoped", icon: "users", tone: "info" },
      { label: "Clarifications", value: "3", trend: "No answer coaching", icon: "message", tone: "warn" },
      { label: "Authority handoffs", value: "2", trend: "Named recipient", icon: "shield", tone: "violet" },
      { label: "Resolved", value: "11", trend: "Decision retained", icon: "check", tone: "good" },
    ],
    tabs: ["Assessment liaison", "Technical liaison", "Decision handover"],
    columns: [
      { key: "name", label: "Liaison" },
      { key: "detail", label: "Question / handover" },
      { key: "channel", label: "Secure channel" },
      { key: "owner", label: "Authorized recipient" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Assessment chair · Responsible AI panel",
        detail: "Clarify approved oral-exam turn order; no content assistance",
        status: "Open",
        owner: "Dr. N. Wekesa",
        channel: "Protected panel liaison",
      },
      {
        name: "AI Product Builder assessor",
        detail: "Confirm reference-sheet version for all candidates",
        status: "Resolved",
        owner: "Amina Mutesi",
        channel: "Assessment operations",
      },
      {
        name: "Integrity Review",
        detail: "INC-2026-0041 transferred with event sequence and candidate statement request",
        status: "Accepted",
        owner: "Integrity Reviewer",
        channel: "Restricted case channel",
      },
      {
        name: "Registry",
        detail: "Late-entry authority requested for candidate CND-2026039",
        status: "Awaiting decision",
        owner: "Registry Officer",
        channel: "Academic records liaison",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-communications-instructor-assessor-liaison-assessor-liaison-board",
    model: "directory",
    layoutGroup: "queue",
  },
  "technical-support": {
    id: "technical-support",
    area: "communications",
    areaLabel: "Communications & Support",
    title: "Technical Support",
    description: "Platform, device, network, audio, video, runtime and recovery assistance.",
    group: "Support",
    icon: "settings",
    family: "technical-support-console",
    source: "static",
    primary: { label: "Open support case", intent: "modal:secure-note", icon: "monitor" },
    secondary: [
      { label: "Connection health", intent: "navigate:command:connection-health", icon: "pulse" },
      { label: "Technical incidents", intent: "navigate:incidents:technical-incidents", icon: "alert" },
    ],
    metrics: [
      { label: "Active cases", value: "5", trend: "Across 3 platforms", icon: "monitor", tone: "warn" },
      { label: "Median response", value: "2.8", trend: "Minutes", icon: "clock", tone: "good" },
      { label: "Candidate reconnected", value: "4", trend: "Work preserved", icon: "pulse", tone: "info" },
      {
        label: "Service incidents",
        value: "1",
        trend: "Labs evaluation harness",
        icon: "flask",
        tone: "violet",
      },
    ],
    tabs: ["Candidate support", "Platform incidents", "Resolved"],
    columns: [
      { key: "name", label: "Support case" },
      { key: "detail", label: "Technical context" },
      { key: "sla", label: "Response target" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "TS-2041 · Candidate reconnect",
        detail: "Academy Secure Room · timer and work preserved",
        status: "Monitoring",
        owner: "Peter Ouma",
        sla: "2 min",
      },
      {
        name: "TS-2042 · Class caption failover",
        detail: "Caption provider changed without ending the oral assessment",
        status: "Resolved",
        owner: "Class Operations",
        sla: "4 min",
      },
      {
        name: "TS-2043 · Labs evaluation harness",
        detail: "Two practical runs paused under continuity procedure",
        status: "Escalated",
        owner: "Labs Technical Lead",
        sla: "6 min",
      },
      {
        name: "TS-2044 · Camera permission",
        detail: "Candidate browser permission reset after update",
        status: "Assigned",
        owner: "Technical Support",
        sla: "3 min",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-communications-technical-support-technical-support-console",
    model: "support",
    layoutGroup: "queue",
  },
  announcements: {
    id: "announcements",
    area: "communications",
    areaLabel: "Communications & Support",
    title: "Announcements",
    description: "Policy-approved room and session broadcasts with audience preview.",
    group: "Communication",
    icon: "bell",
    family: "announcement-composer",
    source: "static",
    primary: { label: "Create session announcement", intent: "modal:broadcast", icon: "send" },
    secondary: [
      {
        label: "Secure communications",
        intent: "navigate:communications:secure-communications",
        icon: "message",
      },
      { label: "Class sessions", intent: "navigate:class:class-exam-sessions", icon: "video" },
    ],
    metrics: [
      { label: "Drafts", value: "2", trend: "Review before send", icon: "file", tone: "warn" },
      { label: "Sent today", value: "5", trend: "Session-scoped", icon: "send", tone: "good" },
      { label: "Languages", value: "4", trend: "Approved translations", icon: "globe", tone: "violet" },
      { label: "Acknowledgement", value: "91", trend: "Candidate receipt", icon: "check", tone: "info" },
    ],
    tabs: ["Compose", "Scheduled", "Sent", "Receipt"],
    columns: [
      { key: "name", label: "Announcement" },
      { key: "detail", label: "Message scope" },
      { key: "time", label: "Delivery" },
      { key: "owner", label: "Author" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "APB-7Q4M · 30-minute reminder",
        detail: "Neutral remaining-time message · English + Luganda",
        status: "Sent",
        owner: "Amina Mutesi",
        time: "15:30 EAT",
      },
      {
        name: "LAB-AWF-8820 · Preflight opening",
        detail: "Candidate entry opens 20 minutes before the practical",
        status: "Scheduled",
        owner: "Grace Namusoke",
        time: "12:40 EAT",
      },
      {
        name: "Class panel caption notice",
        detail: "Live captions and interpreter channel are ready",
        status: "Draft",
        owner: "Daniel Okello",
        time: "08:50 EAT",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: [
      "Audience / assigned session",
      "Announcement type",
      "Primary-language message",
      "Approved translation",
      "Delivery time",
      "Acknowledgement requirement",
      "Expiry",
      "Authorizing reason",
    ],
    statusKey: "status",
    signature: "EP2-communications-announcements-announcement-composer",
    model: "form",
    layoutGroup: "messages",
  },
  "emergency-contacts": {
    id: "emergency-contacts",
    area: "communications",
    areaLabel: "Communications & Support",
    title: "Emergency Contacts",
    description: "Named assessment, technical, accessibility, safeguarding and wellbeing contacts.",
    group: "Support",
    icon: "alert",
    family: "emergency-contact-directory",
    source: "static",
    primary: { label: "Open emergency directory", intent: "modal:emergency", icon: "alert" },
    secondary: [
      { label: "Emergency procedures", intent: "navigate:rooms:emergency-procedures", icon: "shield" },
      { label: "Safeguarding", intent: "navigate:governance:safeguarding", icon: "lock" },
    ],
    metrics: [
      { label: "Current contacts", value: "14", trend: "Verified this week", icon: "users", tone: "good" },
      { label: "24/7 roles", value: "4", trend: "Technical + safety", icon: "clock", tone: "info" },
      {
        label: "Regional backups",
        value: "3",
        trend: "Uganda, Kenya, global",
        icon: "globe",
        tone: "violet",
      },
      { label: "Test overdue", value: "1", trend: "Labs failover contact", icon: "alert", tone: "warn" },
    ],
    tabs: ["Immediate emergency", "Platform support", "Accessibility & safeguarding", "Academic authority"],
    columns: [
      { key: "name", label: "Contact role" },
      { key: "detail", label: "When to contact" },
      { key: "channel", label: "Channel" },
      { key: "owner", label: "Contact" },
      { key: "status", label: "Availability" },
    ],
    rows: [
      {
        name: "Academy Duty Lead",
        detail: "Room-wide pause, continuity and operational authority",
        status: "Available",
        owner: "+256 700 000 201",
        channel: "Secure emergency channel",
      },
      {
        name: "Technical Incident Commander",
        detail: "Academy, Class or Labs platform outage",
        status: "Available",
        owner: "+256 700 000 202",
        channel: "Technical bridge",
      },
      {
        name: "Accessibility Duty Officer",
        detail: "Approved alternative delivery and protected barrier response",
        status: "Available",
        owner: "Protected directory",
        channel: "Accessibility channel",
      },
      {
        name: "Safeguarding Lead",
        detail: "Candidate safety, wellbeing or protected concern",
        status: "On call",
        owner: "Protected directory",
        channel: "Safeguarding channel",
      },
      {
        name: "Assessment Authority",
        detail: "Formal decision about continuation, late entry or academic outcome",
        status: "Available",
        owner: "Registry / Assessment Board",
        channel: "Academic authority channel",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-communications-emergency-contacts-emergency-contact-directory",
    model: "directory",
    layoutGroup: "cards",
  },
  "session-reports": {
    id: "session-reports",
    area: "reports",
    areaLabel: "Reports & Quality",
    title: "Session Reports",
    description: "Complete proctor report, attendance, timing, interventions, incidents and handover.",
    group: "Reports",
    icon: "file",
    family: "session-report-dashboard",
    source: "sessions",
    primary: { label: "Generate session report", intent: "modal:export", icon: "download" },
    secondary: [
      { label: "Report builder", intent: "navigate:reports:report-builder", icon: "file" },
      { label: "Quality review", intent: "navigate:reports:quality-reviews", icon: "check" },
    ],
    metrics: [
      { label: "Draft reports", value: "3", trend: "Human sign-off required", icon: "file", tone: "warn" },
      { label: "Completed", value: "18", trend: "This month", icon: "check", tone: "good" },
      { label: "Documentation", value: "96", trend: "Completeness", icon: "clipboard", tone: "info" },
      { label: "Late reports", value: "1", trend: "Due today", icon: "clock", tone: "violet" },
    ],
    tabs: ["Current reports", "Awaiting sign-off", "Completed"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-reports-session-reports-session-report-dashboard",
    model: "special",
    layoutGroup: "dashboard",
  },
  "incident-analytics": {
    id: "incident-analytics",
    area: "reports",
    areaLabel: "Reports & Quality",
    title: "Incident Analytics",
    description: "Privacy-aware patterns by event category, platform, resolution and response time.",
    group: "Analytics",
    icon: "chart",
    family: "incident-analytics",
    source: "incidents",
    primary: { label: "Export aggregate analytics", intent: "export:incident-analytics", icon: "download" },
    secondary: [
      { label: "Incident queue", intent: "navigate:incidents:incident-queue", icon: "alert" },
      { label: "Quality reviews", intent: "navigate:reports:quality-reviews", icon: "check" },
    ],
    metrics: [
      { label: "Operational incidents", value: "27", trend: "This month", icon: "alert", tone: "info" },
      { label: "Technical share", value: "52", trend: "Kept separate", icon: "pulse", tone: "good" },
      {
        label: "Accessibility barriers",
        value: "11",
        trend: "Delivery improvement",
        icon: "accessibility",
        tone: "violet",
      },
      { label: "Median closure", value: "18", trend: "Minutes", icon: "clock", tone: "warn" },
    ],
    tabs: ["Trend", "Category", "Resolution path", "Minimum-threshold reports"],
    columns: [
      { key: "category", label: "Category" },
      { key: "summary", label: "Factual pattern" },
      { key: "source", label: "Source" },
      { key: "severity", label: "Operational priority" },
      { key: "status", label: "State" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [52, 47, 60, 44, 38, 41, 35, 33],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-reports-incident-analytics-incident-analytics",
    model: "analytics",
    layoutGroup: "analytics",
  },
  "technical-reliability": {
    id: "technical-reliability",
    area: "reports",
    areaLabel: "Reports & Quality",
    title: "Technical Reliability",
    description: "Connectivity, browser, media, Class, Labs and Academy reliability.",
    group: "Analytics",
    icon: "pulse",
    family: "reliability-dashboard",
    source: "static",
    primary: { label: "Run reliability report", intent: "action:test-connection", icon: "pulse" },
    secondary: [
      { label: "Connection health", intent: "navigate:command:connection-health", icon: "monitor" },
      { label: "Technical support", intent: "navigate:communications:technical-support", icon: "help" },
    ],
    metrics: [
      { label: "Academy availability", value: "99.99%", trend: "30 days", icon: "shield", tone: "good" },
      { label: "Class media", value: "99.97%", trend: "30 days", icon: "video", tone: "good" },
      { label: "Labs runtime", value: "99.95%", trend: "30 days", icon: "flask", tone: "warn" },
      {
        label: "Candidate reconnect success",
        value: "96.8%",
        trend: "Work preserved",
        icon: "pulse",
        tone: "info",
      },
    ],
    tabs: ["Platform reliability", "Candidate connectivity", "Continuity tests"],
    columns: [
      { key: "name", label: "Service" },
      { key: "detail", label: "Assessment dependency" },
      { key: "status", label: "Availability" },
      { key: "target", label: "Target" },
      { key: "owner", label: "Owner" },
    ],
    rows: [
      {
        name: "Academy Secure Room",
        detail: "Presence, timers, controls and candidate state",
        status: "99.99%",
        owner: "Academy Platform",
        target: "99.95%",
      },
      {
        name: "Chatoner Class",
        detail: "Media, waiting room, captions and recordings",
        status: "99.97%",
        owner: "Class Platform",
        target: "99.90%",
      },
      {
        name: "Chatoner AI Labs",
        detail: "Practical runtimes and returned evidence",
        status: "99.95%",
        owner: "Labs Platform",
        target: "99.90%",
      },
      {
        name: "Evidence service",
        detail: "Seal, hash, access and custody events",
        status: "100%",
        owner: "Trust Platform",
        target: "99.99%",
      },
      {
        name: "Regional candidate relay",
        detail: "Uganda and East Africa WebSocket relay",
        status: "99.91%",
        owner: "Infrastructure",
        target: "99.90%",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-reports-technical-reliability-reliability-dashboard",
    model: "health",
    layoutGroup: "analytics",
  },
  "accommodation-delivery": {
    id: "accommodation-delivery",
    area: "reports",
    areaLabel: "Reports & Quality",
    title: "Accommodation Delivery",
    description: "Whether approved accommodations were prepared and delivered correctly.",
    group: "Quality",
    icon: "accessibility",
    family: "accommodation-delivery-audit",
    source: "accommodations",
    primary: { label: "Generate delivery report", intent: "modal:export", icon: "download" },
    secondary: [
      {
        label: "Approved accommodations",
        intent: "navigate:accommodations:approved-accommodations",
        icon: "accessibility",
      },
      {
        label: "Accessibility incidents",
        intent: "navigate:accommodations:accessibility-incidents",
        icon: "alert",
      },
    ],
    metrics: [
      { label: "Plans delivered", value: "97", trend: "Percent", icon: "check", tone: "good" },
      { label: "Acknowledgement", value: "100", trend: "Assigned proctors", icon: "users", tone: "info" },
      { label: "Barriers resolved", value: "8", trend: "In-session", icon: "accessibility", tone: "violet" },
      { label: "Missed delivery", value: "1", trend: "Human review open", icon: "alert", tone: "warn" },
    ],
    tabs: ["Delivery status", "Barrier response", "Quality actions"],
    columns: [
      { key: "candidate", label: "Candidate" },
      { key: "type", label: "Approved plan" },
      { key: "session", label: "Session" },
      { key: "instructions", label: "Delivery requirement" },
      { key: "status", label: "Outcome" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-reports-accommodation-delivery-accommodation-delivery-audit",
    model: "analytics",
    layoutGroup: "analytics",
  },
  "proctor-performance": {
    id: "proctor-performance",
    area: "reports",
    areaLabel: "Reports & Quality",
    title: "Proctor Performance",
    description:
      "Coverage, response, documentation, handovers, training and quality review—not learner outcomes.",
    group: "Quality",
    icon: "target",
    family: "proctor-quality-scorecard",
    source: "static",
    primary: { label: "Open self-review", intent: "modal:secure-note", icon: "users" },
    secondary: [
      { label: "Certification", intent: "navigate:governance:proctor-certification", icon: "award" },
      { label: "Practice simulation", intent: "navigate:governance:practice-simulation", icon: "play" },
    ],
    metrics: [
      {
        label: "Documentation quality",
        value: "96",
        trend: "Factual completeness",
        icon: "clipboard",
        tone: "good",
      },
      { label: "Support response", value: "91", trend: "Operational SLA", icon: "help", tone: "info" },
      {
        label: "Policy adherence",
        value: "100",
        trend: "No authority breach",
        icon: "shield",
        tone: "violet",
      },
      { label: "Calibration due", value: "2", trend: "Practice records", icon: "clock", tone: "warn" },
    ],
    tabs: ["My quality", "Calibration", "Learning actions"],
    columns: [
      { key: "name", label: "Quality dimension" },
      { key: "detail", label: "Evidence" },
      { key: "status", label: "Current" },
      { key: "target", label: "Target" },
      { key: "owner", label: "Reviewer" },
    ],
    rows: [
      {
        name: "Factual note quality",
        detail: "Source, time, observation and action separated correctly",
        status: "96%",
        owner: "Quality Reviewer",
        target: "≥90%",
      },
      {
        name: "Candidate support response",
        detail: "Median 2.8 minutes across assigned sessions",
        status: "91%",
        owner: "Operations Lead",
        target: "≥85%",
      },
      {
        name: "Accommodation delivery",
        detail: "Plans acknowledged and delivered as issued",
        status: "100%",
        owner: "Accessibility Office",
        target: "100%",
      },
      {
        name: "Authority-boundary adherence",
        detail: "No grading, coaching or autonomous outcome action",
        status: "100%",
        owner: "Compliance",
        target: "100%",
      },
      {
        name: "Handover completeness",
        detail: "One acknowledgement received after target",
        status: "88%",
        owner: "Proctor Lead",
        target: "≥90%",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-reports-proctor-performance-proctor-quality-scorecard",
    model: "performance",
    layoutGroup: "analytics",
  },
  "report-builder": {
    id: "report-builder",
    area: "reports",
    areaLabel: "Reports & Quality",
    title: "Report Builder",
    description: "Build governed reports using approved fields, thresholds and access scope.",
    group: "Reports",
    icon: "sliders",
    family: "report-builder",
    source: "static",
    primary: { label: "Generate report draft", intent: "modal:export", icon: "file" },
    secondary: [
      { label: "Scheduled reports", intent: "navigate:reports:scheduled-reports", icon: "calendar" },
      { label: "Session reports", intent: "navigate:reports:session-reports", icon: "clipboard" },
    ],
    metrics: [
      { label: "Available datasets", value: "9", trend: "Role-scoped", icon: "database", tone: "info" },
      { label: "Saved templates", value: "6", trend: "Approved fields", icon: "file", tone: "good" },
      { label: "Minimum thresholds", value: "4", trend: "Privacy protection", icon: "lock", tone: "violet" },
      { label: "Drafts", value: "2", trend: "Human sign-off", icon: "clock", tone: "warn" },
    ],
    tabs: ["Data selection", "Fields & filters", "Preview", "Approval"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: [
      "Report purpose",
      "Session / date range",
      "Included operational measures",
      "Minimum group threshold",
      "Excluded protected fields",
      "Recipient",
      "Retention / expiry",
      "Approver",
    ],
    statusKey: "status",
    signature: "EP2-reports-report-builder-report-builder",
    model: "builder",
    layoutGroup: "form",
  },
  "scheduled-reports": {
    id: "scheduled-reports",
    area: "reports",
    areaLabel: "Reports & Quality",
    title: "Scheduled Reports",
    description: "Recurring secure reports for assessment operations and quality teams.",
    group: "Reports",
    icon: "calendar",
    family: "scheduled-report-calendar",
    source: "static",
    primary: { label: "Schedule report", intent: "modal:calendar", icon: "calendar" },
    secondary: [
      { label: "Report builder", intent: "navigate:reports:report-builder", icon: "file" },
      { label: "Delivery history", intent: "export:scheduled-reports", icon: "download" },
    ],
    metrics: [
      {
        label: "Active schedules",
        value: "7",
        trend: "Automated preparation",
        icon: "calendar",
        tone: "info",
      },
      { label: "Awaiting sign-off", value: "2", trend: "No auto-publish", icon: "eye", tone: "warn" },
      { label: "Delivered", value: "18", trend: "This month", icon: "check", tone: "good" },
      { label: "Failed", value: "0", trend: "Delivery healthy", icon: "pulse", tone: "violet" },
    ],
    tabs: ["Upcoming", "Awaiting sign-off", "Delivered", "Paused"],
    columns: [
      { key: "name", label: "Scheduled report" },
      { key: "detail", label: "Scope" },
      { key: "time", label: "Schedule" },
      { key: "owner", label: "Recipient / owner" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Daily live-session operations",
        detail: "Assigned sessions, readiness and support actions",
        status: "Scheduled",
        owner: "Academy Operations",
        time: "17:00 EAT daily",
      },
      {
        name: "Weekly technical reliability",
        detail: "Platform availability, reconnects and continuity tests",
        status: "Scheduled",
        owner: "Technical Operations",
        time: "Monday 09:00",
      },
      {
        name: "Accommodation delivery audit",
        detail: "Minimum-threshold aggregate and named exceptions for protected review",
        status: "Awaiting sign-off",
        owner: "Accessibility Office",
        time: "Friday 16:00",
      },
      {
        name: "Monthly proctor quality review",
        detail: "Documentation, response and policy adherence",
        status: "Scheduled",
        owner: "Assessment Quality",
        time: "1st day monthly",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-reports-scheduled-reports-scheduled-report-calendar",
    model: "schedule",
    layoutGroup: "timeline",
  },
  "quality-reviews": {
    id: "quality-reviews",
    area: "reports",
    areaLabel: "Reports & Quality",
    title: "Quality Reviews",
    description: "Human sampling, calibration, feedback, corrective action and continuous improvement.",
    group: "Quality",
    icon: "check",
    family: "quality-review-workbench",
    source: "static",
    primary: { label: "Start quality review", intent: "modal:secure-note", icon: "check" },
    secondary: [
      { label: "Proctor performance", intent: "navigate:reports:proctor-performance", icon: "users" },
      { label: "Policies", intent: "navigate:governance:proctoring-policies", icon: "book" },
    ],
    metrics: [
      { label: "Open reviews", value: "4", trend: "Named reviewer", icon: "eye", tone: "warn" },
      { label: "Actions accepted", value: "9", trend: "Owners assigned", icon: "check", tone: "good" },
      { label: "Repeat issues", value: "1", trend: "Handover timing", icon: "history", tone: "info" },
      { label: "Policy breaches", value: "0", trend: "No authority breach", icon: "shield", tone: "violet" },
    ],
    tabs: ["Open reviews", "Improvement actions", "Closed"],
    columns: [
      { key: "name", label: "Quality review" },
      { key: "detail", label: "Finding / positive control" },
      { key: "due", label: "Due" },
      { key: "owner", label: "Action owner" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "QAR-2026-044 · Handover acknowledgement",
        detail: "One receiving proctor acknowledged 7 minutes after target",
        status: "Action assigned",
        owner: "Proctor Lead",
        due: "18 Oct",
      },
      {
        name: "QAR-2026-045 · Labs preflight consistency",
        detail: "One runtime-safety check was completed after candidate entry window opened",
        status: "Review open",
        owner: "Labs Operations",
        due: "19 Oct",
      },
      {
        name: "QAR-2026-046 · Accessibility recovery notes",
        detail: "Factual note template improved to separate barrier from diagnosis",
        status: "Implemented",
        owner: "Accessibility Office",
        due: "Complete",
      },
      {
        name: "QAR-2026-047 · Technical incident separation",
        detail: "All 14 connection cases remained outside integrity review unless separate facts existed",
        status: "Verified",
        owner: "Quality Reviewer",
        due: "Complete",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-reports-quality-reviews-quality-review-workbench",
    model: "review",
    layoutGroup: "board",
  },
  "proctoring-policies": {
    id: "proctoring-policies",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Proctoring Policies",
    description: "Approved procedures by assessment type, platform, age, location and risk level.",
    group: "Policy",
    icon: "book",
    family: "policy-library",
    source: "static",
    primary: { label: "Acknowledge policy update", intent: "modal:policy-confirmation", icon: "check" },
    secondary: [
      { label: "Authority boundaries", intent: "navigate:governance:authority-boundaries", icon: "shield" },
      { label: "Certification", intent: "navigate:governance:proctor-certification", icon: "award" },
    ],
    metrics: [
      { label: "Current policies", value: "14", trend: "Versioned", icon: "book", tone: "good" },
      { label: "Acknowledged", value: "13", trend: "One due", icon: "check", tone: "warn" },
      { label: "Jurisdiction profiles", value: "5", trend: "Session-resolved", icon: "globe", tone: "info" },
      { label: "Retired policies", value: "7", trend: "Read-only archive", icon: "archive", tone: "violet" },
    ],
    tabs: ["Current policies", "Acknowledgements", "Jurisdiction profiles", "Archive"],
    columns: [
      { key: "name", label: "Policy" },
      { key: "detail", label: "Scope" },
      { key: "version", label: "Version" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Acknowledgement" },
    ],
    rows: [
      {
        name: "Remote assessment operations",
        detail: "Candidate entry, presence, support, controls and handover",
        status: "Acknowledged",
        owner: "Assessment Operations",
        version: "v6.1",
      },
      {
        name: "Evidence minimization and custody",
        detail: "Purpose, field scope, retention, access and export",
        status: "Acknowledged",
        owner: "Trust & Compliance",
        version: "v4.8",
      },
      {
        name: "Automated signal use",
        detail: "Signals request attention and cannot create findings or outcomes",
        status: "Acknowledgement due",
        owner: "Responsible AI Office",
        version: "v3.2",
      },
      {
        name: "Chatoner Class assessment media",
        detail: "Admission, recording, captions and panel privacy",
        status: "Acknowledged",
        owner: "Class Operations",
        version: "v5.0",
      },
      {
        name: "Chatoner AI Labs practical assessment",
        detail: "Intended runtime, tools, evidence and platform boundary",
        status: "Acknowledged",
        owner: "Labs Operations",
        version: "v4.3",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-proctoring-policies-policy-library",
    model: "policy",
    layoutGroup: "policy",
  },
  "authority-boundaries": {
    id: "authority-boundaries",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Integrity & Authority Boundaries",
    description: "What a proctor may do, what requires another role and what no automated signal may decide.",
    group: "Policy",
    icon: "shield",
    family: "authority-matrix",
    source: "static",
    primary: { label: "Open role boundary", intent: "modal:scope-boundary", icon: "shield" },
    secondary: [
      { label: "Proctoring policies", intent: "navigate:governance:proctoring-policies", icon: "book" },
      { label: "Practice simulation", intent: "navigate:governance:practice-simulation", icon: "play" },
    ],
    metrics: [
      { label: "Proctor permissions", value: "18", trend: "Session-scoped", icon: "shield", tone: "info" },
      { label: "Prohibited actions", value: "11", trend: "Technically blocked", icon: "lock", tone: "good" },
      { label: "Human authorities", value: "7", trend: "Separate roles", icon: "users", tone: "violet" },
      { label: "Boundary reviews", value: "0", trend: "No current breach", icon: "check", tone: "warn" },
    ],
    tabs: ["May do", "May not do", "Human authorities", "Audit"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-authority-boundaries-authority-matrix",
    model: "special",
    layoutGroup: "controls",
  },
  "privacy-data-minimization": {
    id: "privacy-data-minimization",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Privacy & Data Minimization",
    description: "Purpose, consent, least visibility, redaction, retention and candidate rights.",
    group: "Trust",
    icon: "lock",
    family: "privacy-control-centre",
    source: "static",
    primary: { label: "Review privacy boundary", intent: "modal:scope-boundary", icon: "lock" },
    secondary: [
      { label: "Retention & access", intent: "navigate:evidence:retention-access", icon: "clock" },
      { label: "Labs data boundaries", intent: "navigate:labs:labs-data-boundaries", icon: "flask" },
    ],
    metrics: [
      { label: "Data fields visible", value: "27", trend: "Purpose-filtered", icon: "eye", tone: "info" },
      {
        label: "Protected fields hidden",
        value: "41",
        trend: "Diagnosis, unrelated work",
        icon: "lock",
        tone: "good",
      },
      { label: "Active access grants", value: "7", trend: "Expiring", icon: "users", tone: "violet" },
      {
        label: "Privacy exceptions",
        value: "0",
        trend: "No unauthorized access",
        icon: "shield",
        tone: "warn",
      },
    ],
    tabs: ["Displayed data", "Protected fields", "Purpose grants", "Access audit"],
    columns: [
      { key: "name", label: "Data class" },
      { key: "detail", label: "Minimization rule" },
      { key: "purpose", label: "Declared purpose" },
      { key: "owner", label: "Authority" },
      { key: "status", label: "Access" },
    ],
    rows: [
      {
        name: "Candidate identity",
        detail: "Name, candidate number and approved verification state",
        status: "Visible",
        owner: "Assigned proctor",
        purpose: "Session admission",
      },
      {
        name: "Accommodation instruction",
        detail: "Only delivery instruction; diagnosis and full case record hidden",
        status: "Limited",
        owner: "Accessibility Office",
        purpose: "Accessible delivery",
      },
      {
        name: "Labs prompt and files",
        detail: "Hidden unless practical protocol grants an exact operational purpose",
        status: "Protected",
        owner: "Labs Platform",
        purpose: "Not available",
      },
      {
        name: "Assessment response content",
        detail:
          "Not visible to proctor except where a specific technical support path exposes a minimum view",
        status: "Protected",
        owner: "Assessment Service",
        purpose: "Assessor only",
      },
      {
        name: "Incident evidence",
        detail: "Visible by evidence reference and purpose-limited access",
        status: "Limited",
        owner: "Integrity Review",
        purpose: "Factual handover",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-privacy-data-minimization-privacy-control-centre",
    model: "boundary",
    layoutGroup: "controls",
  },
  safeguarding: {
    id: "safeguarding",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Safeguarding",
    description: "Age-aware procedures, protected escalation and restricted evidence access.",
    group: "Trust",
    icon: "heart",
    family: "safeguarding-runbook",
    source: "static",
    primary: { label: "Open protected safeguarding path", intent: "modal:emergency", icon: "shield" },
    secondary: [
      { label: "Emergency contacts", intent: "navigate:communications:emergency-contacts", icon: "users" },
      { label: "Candidate support", intent: "navigate:communications:candidate-help-queue", icon: "help" },
    ],
    metrics: [
      { label: "Protected cases", value: "1", trend: "Restricted details", icon: "lock", tone: "warn" },
      { label: "Duty contacts", value: "3", trend: "Current", icon: "users", tone: "good" },
      { label: "Rehearsal current", value: "Yes", trend: "Completed 8 Oct", icon: "check", tone: "info" },
      {
        label: "General dashboard detail",
        value: "0",
        trend: "Need-to-know only",
        icon: "eye",
        tone: "violet",
      },
    ],
    tabs: ["Immediate safety", "Wellbeing support", "Protected handover", "Training"],
    columns: [
      { key: "name", label: "Situation" },
      { key: "detail", label: "Proctor response" },
      { key: "priority", label: "Priority" },
      { key: "owner", label: "Protected authority" },
      { key: "status", label: "Readiness" },
    ],
    rows: [
      {
        name: "Immediate physical safety",
        detail: "Prioritize safety, stop unnecessary monitoring and contact emergency services / duty lead",
        status: "Runbook ready",
        owner: "Safeguarding Lead",
        priority: "Immediate",
      },
      {
        name: "Candidate distress or wellbeing concern",
        detail: "Offer neutral support, preserve privacy and hand over through protected channel",
        status: "Runbook ready",
        owner: "Wellbeing Support",
        priority: "Urgent",
      },
      {
        name: "Child or vulnerable-person concern",
        detail: "Use the designated safeguarding pathway; do not investigate within the exam room",
        status: "Restricted",
        owner: "Designated Safeguarding Officer",
        priority: "Immediate",
      },
      {
        name: "Protected incident evidence",
        detail: "Minimum necessary reference only in general proctor views",
        status: "Protected",
        owner: "Safeguarding Case Service",
        priority: "Need-to-know",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-safeguarding-safeguarding-runbook",
    model: "runbook",
    layoutGroup: "brief",
  },
  "proctor-certification": {
    id: "proctor-certification",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Proctor Certification",
    description: "Required training, assessments, expiry, refreshers and platform-specific authorization.",
    group: "Training",
    icon: "certificate",
    family: "certification-path",
    source: "static",
    primary: { label: "Continue certification", intent: "modal:policy-confirmation", icon: "award" },
    secondary: [
      { label: "Practice simulation", intent: "navigate:governance:practice-simulation", icon: "play" },
      { label: "Quality scorecard", intent: "navigate:reports:proctor-performance", icon: "chart" },
    ],
    metrics: [
      { label: "Certification", value: "Current", trend: "Expires 30 Sep 2027", icon: "award", tone: "good" },
      { label: "Modules complete", value: "12", trend: "Of 12", icon: "check", tone: "info" },
      { label: "Practice records", value: "8", trend: "2 calibration tasks due", icon: "file", tone: "warn" },
      {
        label: "Policy acknowledgements",
        value: "13",
        trend: "One update due",
        icon: "book",
        tone: "violet",
      },
    ],
    tabs: ["Readiness", "Training modules", "Calibration", "Renewal"],
    columns: [
      { key: "name", label: "Certification requirement" },
      { key: "detail", label: "Competency" },
      { key: "score", label: "Result" },
      { key: "owner", label: "Reviewer" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Role and authority boundaries",
        detail: "Observe, support, document and escalate; no grading or misconduct decision",
        status: "Complete",
        owner: "Academy Training",
        score: "100%",
      },
      {
        name: "Factual incident documentation",
        detail: "Separate source, observation, action, evidence and outcome authority",
        status: "Complete",
        owner: "Quality Team",
        score: "96%",
      },
      {
        name: "Accessibility and accommodations",
        detail: "Apply approved plans and use accessible alternatives",
        status: "Complete",
        owner: "Accessibility Office",
        score: "100%",
      },
      {
        name: "Class and Labs handoffs",
        detail: "Independent platform validation and scoped destinations",
        status: "Complete",
        owner: "Platform Operations",
        score: "94%",
      },
      {
        name: "Calibration records",
        detail: "Two factual-note simulations remain before the next quarterly review",
        status: "Due",
        owner: "Proctor Lead",
        score: "6/8",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-proctor-certification-certification-path",
    model: "certification",
    layoutGroup: "board",
  },
  "practice-simulation": {
    id: "practice-simulation",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Practice Simulation",
    description:
      "Safe rehearsal of joins, signals, interventions, evidence and handover without real candidates.",
    group: "Training",
    icon: "play",
    family: "practice-simulator",
    source: "static",
    primary: { label: "Start proctor simulation", intent: "action:start-simulation", icon: "play" },
    secondary: [
      { label: "Certification", intent: "navigate:governance:proctor-certification", icon: "award" },
      { label: "Authority boundaries", intent: "navigate:governance:authority-boundaries", icon: "shield" },
    ],
    metrics: [
      { label: "Scenarios", value: "8", trend: "Role-specific", icon: "play", tone: "info" },
      { label: "Completed", value: "6", trend: "Human-reviewed", icon: "check", tone: "good" },
      { label: "Calibration due", value: "2", trend: "Factual notes", icon: "clock", tone: "warn" },
      { label: "Best practice", value: "96%", trend: "Documentation quality", icon: "chart", tone: "violet" },
    ],
    tabs: ["Scenario", "Your action", "Evidence record", "Reviewer feedback"],
    columns: [
      { key: "name", label: "Simulation scenario" },
      { key: "detail", label: "Expected role response" },
      { key: "score", label: "Result" },
      { key: "owner", label: "Reviewer" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Candidate disconnects during upload",
        detail: "Preserve work, technical support, timer authority and factual handover",
        status: "Complete",
        owner: "Quality Reviewer",
        score: "95%",
      },
      {
        name: "Second voice briefly audible",
        detail: "Record duration and source; do not conclude misconduct",
        status: "Complete",
        owner: "Integrity Training",
        score: "98%",
      },
      {
        name: "Screen-reader focus stops moving",
        detail: "Apply approved accessibility recovery and protect timer",
        status: "Due",
        owner: "Accessibility Trainer",
        score: "Not started",
      },
      {
        name: "Labs evaluation service unavailable",
        detail: "Use continuity procedure; do not improvise covert evidence capture",
        status: "Due",
        owner: "Labs Operations",
        score: "Not started",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-practice-simulation-practice-simulator",
    model: "simulation",
    layoutGroup: "form",
  },
  "language-accessibility": {
    id: "language-accessibility",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Language & Accessibility",
    description: "Primary language, translation, captions, keyboard use, contrast and motion preferences.",
    group: "Preferences",
    icon: "globe",
    family: "language-accessibility-settings",
    source: "static",
    primary: { label: "Save language settings", intent: "action:save-settings", icon: "globe" },
    secondary: [
      {
        label: "Captions & interpretation",
        intent: "navigate:accommodations:captions-interpretation",
        icon: "message",
      },
      { label: "Candidate statements", intent: "navigate:incidents:candidate-statements", icon: "file" },
    ],
    metrics: [
      { label: "Primary language", value: "English", trend: "Interface", icon: "globe", tone: "info" },
      { label: "Secondary", value: "Luganda", trend: "Translation view", icon: "message", tone: "violet" },
      { label: "Captions", value: "On", trend: "Class handoff", icon: "check", tone: "good" },
      {
        label: "Original retained",
        value: "Yes",
        trend: "Statements and evidence",
        icon: "lock",
        tone: "warn",
      },
    ],
    tabs: ["Language", "Translation", "Captions", "Display accessibility"],
    columns: [
      { key: "name", label: "Record" },
      { key: "detail", label: "Operational detail" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Owner" },
    ],
    rows: [],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: [
      "Primary interface language",
      "Secondary translation language",
      "Retain original content",
      "Live captions default",
      "Reduced motion",
      "Higher contrast",
      "Compact density",
      "Signal sounds",
    ],
    statusKey: "status",
    signature: "EP2-governance-language-accessibility-language-accessibility-settings",
    model: "special",
    layoutGroup: "form",
  },
  notifications: {
    id: "notifications",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Notifications",
    description: "Assignment, live-room, incident, handover, training and system-health alerts.",
    group: "Preferences",
    icon: "bell",
    family: "notification-preferences",
    source: "static",
    primary: { label: "Save notification rules", intent: "action:save-settings", icon: "bell" },
    secondary: [
      { label: "Priority signals", intent: "navigate:command:priority-signals", icon: "alert" },
      {
        label: "Secure communications",
        intent: "navigate:communications:secure-communications",
        icon: "message",
      },
    ],
    metrics: [
      { label: "Urgent rules", value: "4", trend: "Always visible", icon: "alert", tone: "warn" },
      {
        label: "Desktop notifications",
        value: "On",
        trend: "Assigned scope only",
        icon: "bell",
        tone: "good",
      },
      { label: "Signal sounds", value: "On", trend: "Non-alarming cue", icon: "pulse", tone: "info" },
      { label: "Quiet-hour exceptions", value: "2", trend: "Live rooms only", icon: "clock", tone: "violet" },
    ],
    tabs: ["Urgent signals", "Messages", "Schedules", "Quiet hours"],
    columns: [
      { key: "name", label: "Notification event" },
      { key: "detail", label: "Delivery rule" },
      { key: "channel", label: "Channel" },
      { key: "owner", label: "Authority / preference" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Room emergency",
        detail: "Visual + sound + desktop notification; cannot be silenced during assigned live room",
        status: "Enabled",
        owner: "Policy enforced",
        channel: "All",
      },
      {
        name: "Candidate help request",
        detail: "Desktop and in-app notification for assigned candidates",
        status: "Enabled",
        owner: "Amina Mutesi",
        channel: "Desktop + in-app",
      },
      {
        name: "New automated signal",
        detail: "In-app cue; requires human review before incident creation",
        status: "Enabled",
        owner: "Amina Mutesi",
        channel: "In-app",
      },
      {
        name: "Schedule reminder",
        detail: "30 minutes before preflight and 10 minutes before handover",
        status: "Enabled",
        owner: "Amina Mutesi",
        channel: "Desktop",
      },
      {
        name: "Completed export",
        detail: "In-app notification with expiring download grant",
        status: "Enabled",
        owner: "Trust Operations",
        channel: "In-app",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-notifications-notification-preferences",
    model: "settings",
    layoutGroup: "controls",
  },
  "security-sessions": {
    id: "security-sessions",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Security & Sessions",
    description: "MFA, session timeout, trusted devices, re-authentication and emergency revocation.",
    group: "Security",
    icon: "key",
    family: "security-session-manager",
    source: "static",
    primary: { label: "Review active session", intent: "modal:secure-note", icon: "lock" },
    secondary: [
      { label: "Sign out other sessions", intent: "action:revoke-sessions", icon: "logout" },
      {
        label: "Connected platforms",
        intent: "navigate:governance:connected-platform-settings",
        icon: "link",
      },
    ],
    metrics: [
      { label: "Active sessions", value: "2", trend: "Current devices", icon: "monitor", tone: "info" },
      { label: "MFA", value: "Verified", trend: "Recent for controls", icon: "shield", tone: "good" },
      { label: "Platform handoffs", value: "1", trend: "Expires in 3 minutes", icon: "link", tone: "violet" },
      { label: "Security alerts", value: "0", trend: "No anomaly", icon: "check", tone: "warn" },
    ],
    tabs: ["Active sessions", "MFA & recovery", "Handoff tokens", "Security events"],
    columns: [
      { key: "name", label: "Session / token" },
      { key: "detail", label: "Security context" },
      { key: "last", label: "Last activity / expiry" },
      { key: "owner", label: "Actor / issuer" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Windows desktop · Kampala",
        detail: "Chrome 151 · current session · MFA verified 14:01",
        status: "Current",
        owner: "Amina Mutesi",
        last: "Now",
      },
      {
        name: "Android phone · Kampala",
        detail: "Notification companion · no room controls",
        status: "Active",
        owner: "Amina Mutesi",
        last: "8 min ago",
      },
      {
        name: "Class handoff token",
        detail: "Audience class.chatoner.com · one-time · oral session",
        status: "Expires soon",
        owner: "Identity Service",
        last: "3 min",
      },
      {
        name: "Labs handoff token",
        detail: "Consumed for session sess-lab-2026-10-16",
        status: "Consumed",
        owner: "Identity Service",
        last: "Yesterday",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-security-sessions-security-session-manager",
    model: "settings",
    layoutGroup: "controls",
  },
  "connected-platform-settings": {
    id: "connected-platform-settings",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Connected Platform Settings",
    description: "Academy, Class and Labs trust, handoff, return, scope and health settings.",
    group: "Security",
    icon: "link",
    family: "platform-connection-settings",
    source: "static",
    primary: { label: "Test connected platform", intent: "action:test-connection", icon: "link" },
    secondary: [
      { label: "Connection health", intent: "navigate:command:connection-health", icon: "pulse" },
      { label: "Labs boundaries", intent: "navigate:labs:labs-data-boundaries", icon: "lock" },
    ],
    metrics: [
      {
        label: "Academy backend",
        value: "Connected",
        trend: "NestJS services",
        icon: "shield",
        tone: "good",
      },
      { label: "Chatoner Class", value: "Connected", trend: "Signed handoff", icon: "video", tone: "info" },
      {
        label: "Chatoner AI Labs",
        value: "Connected",
        trend: "Exact destination",
        icon: "flask",
        tone: "violet",
      },
      { label: "Webhook issues", value: "0", trend: "All callbacks current", icon: "link", tone: "warn" },
    ],
    tabs: ["Academy", "Chatoner Class", "Chatoner AI Labs", "Audit"],
    columns: [
      { key: "name", label: "Connection" },
      { key: "detail", label: "Purpose" },
      { key: "endpoint", label: "Boundary" },
      { key: "owner", label: "Platform owner" },
      { key: "status", label: "State" },
    ],
    rows: [
      {
        name: "Academy Proctor API",
        detail: "Assignments, sessions, readiness, incidents, evidence and reports",
        status: "Connected",
        owner: "Academy Platform",
        endpoint: "academy.chatoner.com",
      },
      {
        name: "Chatoner Class handoff",
        detail: "One-time audience-bound token for live media and presence",
        status: "Connected",
        owner: "Class Platform",
        endpoint: "class.chatoner.com",
      },
      {
        name: "Chatoner AI Labs handoff",
        detail: "Exact proctor destination, intended Lab and candidate-scope hash",
        status: "Connected",
        owner: "Labs Platform",
        endpoint: "labs.chatoner.com",
      },
      {
        name: "Evidence callback",
        detail: "Sealed manifest and access event return",
        status: "Healthy",
        owner: "Trust Platform",
        endpoint: "Kafka + API",
      },
      {
        name: "Presence event stream",
        detail: "Assigned-session WebSocket/SSE fan-out",
        status: "Healthy",
        owner: "Realtime Gateway",
        endpoint: "Academy SSE",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-connected-platform-settings-platform-connection-settings",
    model: "connections",
    layoutGroup: "connection",
  },
  "proctor-help": {
    id: "proctor-help",
    area: "governance",
    areaLabel: "Policies, Training & Settings",
    title: "Proctor Help",
    description: "Procedures, role guidance, contact paths and support resources.",
    group: "Support",
    icon: "help",
    family: "proctor-help-centre",
    source: "static",
    primary: { label: "Contact Proctor Support", intent: "modal:settings-help", icon: "help" },
    secondary: [
      { label: "Emergency contacts", intent: "navigate:communications:emergency-contacts", icon: "alert" },
      { label: "Practice simulation", intent: "navigate:governance:practice-simulation", icon: "play" },
    ],
    metrics: [
      { label: "Help guides", value: "48", trend: "Searchable", icon: "book", tone: "info" },
      { label: "Operational playbooks", value: "12", trend: "Role-scoped", icon: "shield", tone: "good" },
      { label: "Support response", value: "2.8 min", trend: "Live sessions", icon: "help", tone: "violet" },
      {
        label: "Open support ticket",
        value: "1",
        trend: "Factual-note calibration",
        icon: "message",
        tone: "warn",
      },
    ],
    tabs: ["Popular guides", "Live support", "Troubleshooting", "Training"],
    columns: [
      { key: "name", label: "Help topic" },
      { key: "detail", label: "Guidance" },
      { key: "category", label: "Category" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Type" },
    ],
    rows: [
      {
        name: "Candidate cannot reconnect",
        detail: "Preserve work and timer; use the approved re-entry path",
        status: "Guide",
        owner: "Technical Operations",
        category: "Live room",
      },
      {
        name: "When to record an incident",
        detail: "Record observable facts when a real event requires a durable record",
        status: "Guide",
        owner: "Integrity Operations",
        category: "Incidents",
      },
      {
        name: "Apply an approved accommodation",
        detail: "Follow the issued instructions; do not alter or infer a diagnosis",
        status: "Guide",
        owner: "Accessibility Office",
        category: "Accessibility",
      },
      {
        name: "Open Chatoner Class or Labs",
        detail: "Use the assigned session handoff; destination validates independently",
        status: "Guide",
        owner: "Platform Operations",
        category: "Connected platforms",
      },
      {
        name: "Emergency and safeguarding",
        detail: "Prioritize safety, minimize monitoring and use the protected pathway",
        status: "Runbook",
        owner: "Safeguarding Lead",
        category: "Emergency",
      },
    ],
    notes: [
      {
        title: "Minimum necessary access",
        body: "Only assigned sessions and the data required for the selected operational purpose are visible.",
        icon: "lock",
      },
      {
        title: "Human decision boundary",
        body: "Signals and observations may request attention; they do not establish misconduct or an academic outcome.",
        icon: "shield",
      },
    ],
    workflow: [
      {
        title: "Confirm assigned scope",
        detail: "Verify session, candidate scope, role and escalation contacts.",
        status: "Complete",
        owner: "Exam Proctor",
      },
      {
        title: "Perform the operational task",
        detail: "Use the approved procedure and record factual actions.",
        status: "In progress",
        owner: "Exam Proctor",
      },
      {
        title: "Preserve and hand over",
        detail: "Link authorized evidence and name the next responsible role.",
        status: "Pending",
        owner: "Exam Proctor",
      },
    ],
    chart: [72, 79, 84, 76, 88, 91, 86, 94],
    fields: ["Operational reason", "Assigned session", "Factual note", "Next owner"],
    statusKey: "status",
    signature: "EP2-governance-proctor-help-proctor-help-centre",
    model: "help",
    layoutGroup: "cards",
  },
};
